import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { Button, Card, Space, Modal, Input, Select, message, Switch, Tooltip, Row, Col, Tag, Form, TimePicker, Typography, Spin, Alert } from 'antd';
import { 
  EnvironmentOutlined, 
  PhoneOutlined, 
  MailOutlined, 
  InfoCircleOutlined, 
  DownloadOutlined, 
  ReloadOutlined,
  UndoOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  MinusOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SettingOutlined,
  FileTextOutlined,
  SendOutlined,
  UserOutlined,
  TeamOutlined,
  MessageOutlined,
  AimOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getSitesByOrganization, getOrgSiteTypes, SITE_TYPE_CONFIG, SITE_STATUS_CONFIG, SITE_STATUS, updateSiteStatus } from '../../services/siteService';
import { SmartAutomationService } from '../../services/smartAutomationService';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import SiteSelectionPanel from './SiteSelectionPanel';
import MassContactModal from './MassContactModal';
import AdvancedDrawingTools from './AdvancedDrawingTools';
import HazardOverlayLayer from './HazardOverlayLayer';
import SiteListPopup from './SiteListPopup';
import { createIncidentLog } from '../../utils/activityLogger';
import SMSSegmentIndicator from '../SMSSegmentIndicator';

const { TextArea } = Input;

/**
 * MapCore - Core Google Maps component with site management
 * Supports both main map view and crisis map sub-tab
 */
const MapCore = ({ 
  mapId = 'crisis-compass-map',
  height = '400px',
  showDrawingTools = true,
  showSiteSelection = true,
  initialCenter = { lat: -25.2744, lng: 133.7751 }, // Australia center
  initialZoom = 5,
  crisisLocation = null,
  onSitesSelected = null,
  onMapLoad = null,
  onSitesLoaded = null,
  onCrisisLocationDrag = null, // 🆕 PHASE 4: Callback for when crisis location is manually repositioned
  className = '',
  // Measurement tool props
  onMeasurementModeChange = null,
  onMeasurementStart = null,
  onMeasurementClear = null,
  // Phase C-Alpha: Unit preference prop
  unitPreference = 'km',
  // Phase 2C-Alpha: Ring styling configuration
  ringStyleConfig = {
    colorScheme: 'red', // 'red', 'blue', 'green', 'purple', 'orange', 'custom'
    customColors: {
      primary: '#ff0000',
      rings: '#ff0000'
    },
    opacity: {
      primary: 0.3,
      rings: 0.2,
      fill: 0.1
    },
    strokeWeight: {
      primary: 3,
      rings: 2
    }
  },
  // Site display props
  showSiteIcons = true,
  siteColorMode = 'status',
  // Advanced drawing tools integration
  useAdvancedDrawingTools = false,
  // Circle detection popup callbacks (for Dashboard positioning)
  onSitePopupStateChange = null, // Callback when popup visibility changes
  onSitePopupDataChange = null,  // Callback when popup data changes
  // Circle completion callback
  onCircleComplete = null        // Callback when circle drawing is completed
}) => {
  console.log('🔴🔴🔴 MAPCORE COMPONENT STARTING 🔴🔴🔴');
  console.log('🔴 Props received:', { 
    mapId, 
    height, 
    showSiteIcons, 
    crisisLocation: !!crisisLocation,
    className 
  });
  console.log('🔴 Current screen width:', window.innerWidth);
  console.log('🔴 Should use mobile map?', window.innerWidth <= 768);
  
  // DEBUG: Log what callback we received
  console.log('🔍 MapCore: Component received onSitesLoaded prop:', !!onSitesLoaded, typeof onSitesLoaded);
  
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null); // NEW: Container ref for WeatherLegend positioning
  const googleMapRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const [sites, setSites] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [drawingManager, setDrawingManager] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [userLocationMarker, setUserLocationMarker] = useState(null);
  const [showSitePopup, setShowSitePopup] = useState(false);
  const [circleDetectedSites, setCircleDetectedSites] = useState([]);
  const [activeCircle, setActiveCircle] = useState(null);
  const [infoWindow, setInfoWindow] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // 🆕 PHASE 4: Crisis location marker state and drag feedback
  const [crisisLocationMarker, setCrisisLocationMarker] = useState(null);
  const [isDraggingCrisisLocation, setIsDraggingCrisisLocation] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState(null);
  
  const measurementFunctionsRef = useRef({
    // Initialize with default values to prevent timing issues
    measurementMode: 'idle',
    measurements: [],
    startMeasurement: () => console.warn('📏 startMeasurement not ready yet'),
    clearMeasurement: () => console.warn('📏 clearMeasurement not ready yet'),
    clearAllMeasurements: () => console.warn('📏 clearAllMeasurements not ready yet'),
    deleteMeasurement: () => console.warn('📏 deleteMeasurement not ready yet'),
    toggleMeasurementVisibility: () => console.warn('📏 toggleMeasurementVisibility not ready yet'),
    updateMeasurementLabel: () => console.warn('📏 updateMeasurementLabel not ready yet'),
    addCircleMeasurement: () => console.warn('📏 addCircleMeasurement not ready yet'),
    removeCircleMeasurement: () => console.warn('📏 removeCircleMeasurement not ready yet'),
    updateCircleMeasurementVisibility: () => console.warn('📏 updateCircleMeasurementVisibility not ready yet')
  });
  const [orgSiteTypes, setOrgSiteTypes] = useState([]);
  const [measurementMode, setMeasurementMode] = useState('idle'); // 'idle', 'first-click', 'second-click', 'complete'
  const [measurementData, setMeasurementData] = useState(null);
  const [measurementMarkers, setMeasurementMarkers] = useState([]);
  const [measurementLine, setMeasurementLine] = useState(null);
  const [measurementInfoWindow, setMeasurementInfoWindow] = useState(null);
  const [measurements, setMeasurements] = useState([]); // Array of completed measurements
  const [measurementIdCounter, setMeasurementIdCounter] = useState(1);
  const measurementObjectsRef = useRef(new Map()); // Track Google Maps objects by measurementId
  const isDirectToggleRef = useRef(false); // Track when we're doing a direct toggle to prevent useEffect recreation
  const [updatingSiteStatus, setUpdatingSiteStatus] = useState(null); // siteId currently being updated
  const [showIndividualSMSModal, setShowIndividualSMSModal] = useState(false);
  const [selectedSiteForSMS, setSelectedSiteForSMS] = useState(null);
  const [sendingIndividualSMS, setSendingIndividualSMS] = useState(false);
  const [showUserLocation, setShowUserLocation] = useState(false);
  const [userLocationError, setUserLocationError] = useState(null);
  const [requestingLocation, setRequestingLocation] = useState(false);
  const [error, setError] = useState(null);

  // Measurement tool state - Variables already declared above

  // PHASE 1: Circle site detection and SMS state - Variables already declared above
  const [individualSMSForm] = Form.useForm();
  


  // USER LOCATION STATE - Variables already declared above

  // Phase C-Alpha: Measurement persistence functions
  const saveMeasurementsToStorage = (measurementsData) => {
    try {
      const dataToSave = {
        measurements: measurementsData,
        version: '1.0',
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem('crisisCompass_measurements', JSON.stringify(dataToSave));
      console.log('📏 Measurements saved to localStorage:', measurementsData.length, 'measurements');
    } catch (error) {
      console.warn('📏 Failed to save measurements to localStorage:', error);
    }
  };

  const loadMeasurementsFromStorage = () => {
    try {
      const saved = localStorage.getItem('crisisCompass_measurements');
      if (saved) {
        const data = JSON.parse(saved);
        if (data.measurements && Array.isArray(data.measurements)) {
          console.log('📏 Measurements loaded from localStorage:', data.measurements.length, 'measurements');
          
          // Extract numeric part from IDs like "line_1", "line_2", etc.
          let maxId = 0;
          data.measurements.forEach(measurement => {
            if (measurement.id) {
              const match = measurement.id.match(/(\d+)$/);
              if (match) {
                const id = parseInt(match[1]);
                if (!isNaN(id) && id > maxId) {
                  maxId = id;
                }
              }
            }
          });
          
          return {
            measurements: data.measurements,
            maxId: maxId
          };
        }
      }
    } catch (error) {
      console.warn('📏 Failed to load measurements from localStorage:', error);
    }
    return { measurements: [], maxId: 0 };
  };

  // Create dynamic site type config from organization's registered types
  const SITE_TYPE_CONFIG = useMemo(() => {
    const config = {};
    
    // Convert org site types to config format - with safety check
    if (orgSiteTypes && Array.isArray(orgSiteTypes)) {
    orgSiteTypes.forEach(type => {
      config[type.name] = {
        color: type.color,
        letter: type.letter
      };
    });
    }
    
    // Fallback for sites with unregistered types
    const fallbackTypes = {
      other: { color: '#95a5a6', letter: 'O' }
    };
    
    return { ...config, ...fallbackTypes };
  }, [orgSiteTypes]);

  // Site status colors and labels - Crisis Management focused
  const SITE_STATUS_CONFIG = {
    active: { color: '#27ae60', label: 'Active' },
    degraded: { color: '#f39c12', label: 'Degraded' },
    evacuating: { color: '#e74c3c', label: 'Evacuating' },
    evacuated: { color: '#9b59b6', label: 'Evacuated' },
    closed_and_secure: { color: '#3498db', label: 'Closed and Secure' },
    unknown: { color: '#95a5a6', label: 'Unknown' }
  };

  // TEMPORARY: Quick test function to set diverse site statuses
  const setTestSiteStatuses = async () => {
    if (!sites.length) return;
    
    console.log('🧪 Setting test site statuses for color demonstration...');
    const statusList = ['active', 'degraded', 'evacuating', 'evacuated', 'closed_and_secure', 'unknown'];
    
    for (let i = 0; i < Math.min(sites.length, 6); i++) {
      const site = sites[i];
      const testStatus = statusList[i % statusList.length];
      
      try {
        await updateSiteStatus(site.id, testStatus, currentUser.uid);
        console.log(`🧪 Updated ${site.site_name} to status: ${testStatus}`);
      } catch (error) {
        console.error(`Failed to update ${site.site_name}:`, error);
      }
    }
    
    // Reload sites to reflect changes
    setTimeout(() => {
      loadSites();
      message.success('Test site statuses applied! Toggle to "Color by Status" to see the differences.');
    }, 1000);
  };

  // Phase C-Alpha: Load measurements from localStorage on component mount
  useEffect(() => {
    const { measurements: savedMeasurements, maxId } = loadMeasurementsFromStorage();
    if (savedMeasurements.length > 0) {
      setMeasurements(savedMeasurements);
      setMeasurementIdCounter(maxId + 1);
    }
  }, []);

  // Phase C-Alpha: Auto-save measurements when they change
  useEffect(() => {
    // CRITICAL FIX: Always save measurements, even when empty array
    // This ensures clearAllMeasurements properly persists the empty state
    console.log('💾 Auto-saving measurements to localStorage:', measurements.length);
    saveMeasurementsToStorage(measurements);
  }, [measurements]);

  // Load sites on component mount
  useEffect(() => {
    console.log('🔍 MapCore: useEffect for loading sites triggered', {
      hasCurrentUser: !!currentUser,
      orgId: currentUser?.orgId,
      userEmail: currentUser?.email
    });
    
    if (currentUser?.orgId) {
      console.log('🔍 MapCore: Loading sites for orgId:', currentUser.orgId);
      loadSites();
    } else {
      console.log('🔍 MapCore: No orgId found, setting empty sites array');
      // Show map even without orgId
      setIsLoading(false);
      setSites([]);
    }
  }, [currentUser?.orgId]);

  // TEMPORARY: Expose test function to window for console access
  useEffect(() => {
    window.setTestSiteStatuses = setTestSiteStatuses;
    return () => {
      delete window.setTestSiteStatuses;
    };
  }, [sites, currentUser]);

  // Set global map container reference for WeatherLegend positioning
  console.log('🗺️ ABOUT TO SET UP MAP_CONTAINER_REF useEffect');
  console.log('🗺️ mapContainerRef at setup time:', mapContainerRef);
  
  useEffect(() => {
    console.log('🗺️ MAP_CONTAINER_REF useEffect RUNNING!');
    console.log('🗺️ MapCore setting global MAP_CONTAINER_REF');
    console.log('🗺️ mapContainerRef.current:', mapContainerRef.current);
    console.log('🗺️ mapRef.current:', mapRef.current);
    
    // Always set the reference immediately
    window.MAP_CONTAINER_REF = mapContainerRef;
    
    console.log('🗺️ window.MAP_CONTAINER_REF set to:', window.MAP_CONTAINER_REF);
    
    return () => {
      console.log('🗺️ MAP_CONTAINER_REF useEffect CLEANUP');
      // Clean up global reference on unmount
      delete window.MAP_CONTAINER_REF;
    };
  }, []); // No dependencies - set immediately on mount

  // Create site markers when sites, map, or color mode changes
  useEffect(() => {
    console.log('🗺️ Site markers effect triggered:', {
      hasMap: !!googleMapRef.current,
      sitesCount: sites.length,
      hasInfoWindow: !!infoWindow,
      showSiteIcons: showSiteIcons,
      siteColorMode: siteColorMode,
      sites: sites
    });
    if (googleMapRef.current && infoWindow) {
      if (showSiteIcons && sites.length > 0) {
        console.log('🗺️ Creating site markers for', sites.length, 'sites with color mode:', siteColorMode);
        createSiteMarkers(googleMapRef.current, infoWindow);
      } else {
        console.log('🗺️ Clearing site markers (showSiteIcons:', showSiteIcons, ', sites count:', sites.length, ')');
        // Clear existing markers when sites are hidden
        markers.forEach(marker => marker.setMap(null));
        setMarkers([]);
        
        // Clear site selection when sites are hidden
        if (selectedSites.length > 0) {
          setSelectedSites([]);
          if (onSitesSelected) {
            onSitesSelected([]);
          }
        }
        
        // Close any open info windows
        if (infoWindow) {
          infoWindow.close();
        }
      }
    }
  }, [sites, infoWindow, showSiteIcons, siteColorMode]);

  // USER LOCATION EFFECTS - NEW FEATURE
  useEffect(() => {
    if (googleMapRef.current && userLocation && showUserLocation) {
      createUserLocationMarker(googleMapRef.current);
    }
  }, [userLocation, showUserLocation]);

  // Update user location marker size when zoom changes
  useEffect(() => {
    if (googleMapRef.current && userLocationMarker && showUserLocation) {
      const handleZoomChange = () => {
        updateUserLocationMarkerScale();
      };
      
      const zoomListener = googleMapRef.current.addListener('zoom_changed', handleZoomChange);
      
      return () => {
        if (zoomListener) {
          window.google.maps.event.removeListener(zoomListener);
        }
      };
    }
  }, [userLocationMarker, showUserLocation]);

  // Cleanup user location marker when component unmounts
  useEffect(() => {
    return () => {
      if (userLocationMarker) {
        if (userLocationMarker.setMap) {
          userLocationMarker.setMap(null);
        } else if (userLocationMarker.overlay) {
          userLocationMarker.overlay.setMap(null);
        }
      }
    };
  }, []);

  const loadSites = async () => {
    try {
      setIsLoading(true);
      console.log('🗺️ Loading sites and site types for organization:', currentUser.orgId);
      
      // Load sites and site types in parallel
      const [orgSites, orgTypes] = await Promise.all([
        getSitesByOrganization(currentUser.orgId),
        getOrgSiteTypes(currentUser.orgId)
      ]);
      
      console.log('🗺️ Sites loaded:', orgSites.length, 'sites found');
      console.log('🎨 Site types loaded:', orgTypes.length, 'types found');
      console.log('🗺️ First site status/type:', orgSites[0]?.status, orgSites[0]?.site_type);
      const statusCounts = orgSites.reduce((acc, site) => {
        const status = site.status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      console.log('🗺️ Site status breakdown:', statusCounts);
      
      const typeCounts = orgSites.reduce((acc, site) => {
        const type = site.site_type || 'other';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      console.log('🗺️ Site type breakdown:', typeCounts);
      
      console.log('🗺️ About to setSites and setOrgSiteTypes - this should trigger onSitesLoaded useEffect');
      
      setSites(orgSites);
      setOrgSiteTypes(orgTypes);
      setError(null);
    } catch (error) {
      console.error('🗺️ Error loading sites:', error);
      // Silently handle site loading errors to reduce console noise
      // This is expected when Firebase permissions aren't configured
      setSites([]);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  };

  // 🔄 FIXED: Polling-based updates to avoid Firestore permissions issues
  useEffect(() => {
    if (!currentUser?.orgId) return;

    console.log('🔄 MapCore: Setting up periodic site refresh for SMS updates');
    
    // Refresh sites every 30 seconds to catch SMS updates
    const refreshInterval = setInterval(async () => {
      try {
        console.log('🔄 MapCore: Periodic refresh - checking for site updates');
        const refreshedSites = await getSitesByOrganization(currentUser.orgId);
        
        // Only update if sites actually changed
        const sitesChanged = JSON.stringify(refreshedSites) !== JSON.stringify(sites);
        if (sitesChanged) {
          console.log('🔄 MapCore: Sites changed detected - updating!');
          setSites(refreshedSites);
        }
      } catch (error) {
        console.error('🔄 MapCore: Periodic refresh error:', error);
      }
    }, 30000); // Refresh every 30 seconds

    // Cleanup interval on unmount
    return () => {
      console.log('🔄 MapCore: Cleaning up periodic refresh');
      clearInterval(refreshInterval);
    };
  }, [currentUser?.orgId, sites]);

  // Notify parent when sites are loaded
  useEffect(() => {
    console.log('🔥 MapCore: onSitesLoaded useEffect TRIGGERED!', {
      timestamp: new Date().toLocaleTimeString(),
      hasCallback: !!onSitesLoaded,
      sitesLength: sites.length,
      willCall: !!(onSitesLoaded && sites.length >= 0),
      trigger: 'sites or onSitesLoaded changed',
      firstSite: sites[0] ? { id: sites[0].id, status: sites[0].status } : null
    });
    
    if (onSitesLoaded && sites.length >= 0) {
      // Calculate the working counts that we KNOW are correct
      const statusCounts = sites.reduce((acc, site) => {
        const status = site.status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      
      const typeCounts = sites.reduce((acc, site) => {
        const type = site.site_type || 'other';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
              }, {});
        
        console.log('🔥 MapCore: CALLING onSitesLoaded callback!', {
          sitesLength: sites.length,
          statusCounts,
          typeCounts,
          timestamp: new Date().toLocaleTimeString()
        });
        onSitesLoaded(sites, { statusCounts, typeCounts });
      }
  }, [sites, onSitesLoaded]);

  // Initialize Google Map
  const initializeMap = useCallback((map) => {
    console.log('🗺️ initializeMap callback CALLED!', { map, hasMap: !!map });
    
    if (!map) {
      console.error('🗺️ initializeMap called with null map!');
      return;
    }
    
    console.log('🗺️ Google Map initialized successfully, creating info window...');
    
    // Create info window
    const infoWin = new window.google.maps.InfoWindow();
    setInfoWindow(infoWin);
    console.log('🗺️ Info window created');
    
    // Phase 2C-Alpha: Add zoom change listener for responsive label updates
    window.google.maps.event.addListener(map, 'zoom_changed', () => {
      // Trigger re-render of all measurement objects to update zoom-responsive styling
      setTimeout(() => {
        if (measurementObjectsRef.current && measurementObjectsRef.current.size > 0) {
          measurementObjectsRef.current.forEach((objects, measurementId) => {
            // Update label overlays for circles
            Object.keys(objects).forEach(key => {
              if (key.startsWith('label_') && objects[key] && objects[key].draw) {
                objects[key].draw();
              }
            });
          });
        }
      }, 100); // Small delay to ensure zoom level is updated
    });
    
    // 🔬 SURGICAL FIX: Initialize drawing manager only when AdvancedDrawingTools is NOT handling drawing
    if (showDrawingTools && !useAdvancedDrawingTools) {
      console.log('🔬 MapCore: Creating drawing manager (AdvancedDrawingTools not active)');
      const drawManager = new window.google.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: false
      });
      
      drawManager.setMap(map);
      setDrawingManager(drawManager);
      
      // Handle shape completion
      window.google.maps.event.addListener(drawManager, 'overlaycomplete', (event) => {
        const shape = event.overlay;
        const bounds = getShapeBounds(shape, event.type);
        
        if (bounds) {
          selectSitesInBounds(bounds);
        }
        
        // Stop drawing mode
        drawManager.setDrawingMode(null);
        setIsDrawing(false);
        
        // Remove the shape after selection
        setTimeout(() => {
          shape.setMap(null);
        }, 2000);
      });
    } else if (useAdvancedDrawingTools) {
      console.log('🔬 MapCore: Skipping drawing manager creation (AdvancedDrawingTools active)');
    }
    
    // Store map reference for dynamic event handling
    googleMapRef.current = map;

    // Add crisis location marker if provided
    if (crisisLocation) {
      addCrisisLocationMarker(map, crisisLocation);
    }
    
    // Notify parent component of map load (without sites to avoid infinite loop)
    if (onMapLoad) {
      // Attach measurement functions ref to map instance
      console.log('📏 Attaching measurementFunctionsRef to map instance');
      
      // The measurementFunctionsRef.current will be populated by the useEffect
      // We just attach the ref itself to the map
      map.measurementFunctionsRef = measurementFunctionsRef;
      console.log('📏 measurementFunctionsRef attached to map');
      onMapLoad(map, []);
    }
    
    setIsLoading(false);
  }, [showDrawingTools, useAdvancedDrawingTools, crisisLocation, onMapLoad]);

  // Dynamic map click handler (outside initializeMap to access current state)
  useEffect(() => {
    if (googleMapRef.current) {
      console.log('🗺️ Setting up dynamic click listener, measurementMode:', measurementMode);
      
      const clickListener = window.google.maps.event.addListener(googleMapRef.current, 'click', (event) => {
        console.log('🗺️ Map clicked, measurementMode:', measurementMode);
        
        // 🎯 CRITICAL FIX: Don't interfere with drawing mode
        if (drawingManager && drawingManager.getDrawingMode() !== null) {
          console.log('🎯 DRAWING MODE ACTIVE: Ignoring map click to allow drawing');
          return; // Let Drawing Manager handle the click
        }
        
        // Priority 1: Measurement tool (highest priority)
        if (measurementMode === 'first-click' || measurementMode === 'second-click') {
          console.log('🗺️ Handling measurement click');
          handleMeasurementClick(event);
          return; // Stop event propagation
        }
        
        console.log('🗺️ Normal map interaction');
        // Priority 2: Other interactions (site markers handle their own clicks)
        // This allows normal map panning/interaction when no special mode is active
      });
      
      return () => {
        console.log('🗺️ Removing click listener');
        window.google.maps.event.removeListener(clickListener);
      };
    }
     }, [measurementMode, measurementData]); // Re-attach when measurement state changes

  // Phase 1B: Measurement objects management with separate visibility control
  useEffect(() => {
    if (!googleMapRef.current) return;

    // CRITICAL FIX: Only recreate when actual measurement IDs don't match
    const measurementIds = new Set(measurements.map(m => m.id));
    const objectIds = new Set(measurementObjectsRef.current.keys());
    
    // Check if the sets are identical
    const measurementIdsArray = Array.from(measurementIds).sort();
    const objectIdsArray = Array.from(objectIds).sort();
    const idsMatch = measurementIdsArray.length === objectIdsArray.length && 
                     measurementIdsArray.every((id, index) => id === objectIdsArray[index]);
    
    if (idsMatch) {
      console.log('🔥 SKIP RECREATION: Measurement IDs match object IDs perfectly');
      console.log('🔥 SKIP RECREATION: Measurements:', measurementIdsArray);
      console.log('🔥 SKIP RECREATION: Objects:', objectIdsArray);
      return;
    }

    console.log('🔥 useEffect TRIGGERED - Managing measurement objects:', measurements.length);
    console.log('🔥 useEffect TRIGGERED - Measurement IDs:', measurements.map(m => m.id));
    console.log('🔥 useEffect TRIGGERED - Measurement types:', measurements.map(m => `${m.id}:${m.type}`));
    console.log('🔥 useEffect TRIGGERED - Measurement visibility:', measurements.map(m => `${m.id}:${m.visible}`));
    console.log('🔥 useEffect TRIGGERED - Object IDs:', Array.from(measurementObjectsRef.current.keys()));
    console.log('🔥 BEFORE RECREATION - Map size:', measurementObjectsRef.current.size);
    console.log('🔥 REASON FOR RECREATION: IDs mismatch detected - will recreate ALL objects');
    
    // CRITICAL FIX: Actually remove old objects from map before clearing references
    console.log('🔥 CLEANING UP OLD OBJECTS BEFORE RECREATION');
    measurementObjectsRef.current.forEach((entryGroup, measurementId) => {
      console.log('🔥 REMOVING OLD OBJECTS FOR:', measurementId);
      const { objects = [], overlays = [] } = entryGroup;
      
      // Remove all Google Maps objects from map
      objects.forEach((obj, index) => {
        if (obj && obj.setMap) {
          obj.setMap(null);
          console.log(`🔥 REMOVED OLD OBJECT ${index} FOR ${measurementId}`);
        }
      });
      
      // Remove all custom overlays from map
      overlays.forEach((overlay, index) => {
        if (overlay && overlay.setMap) {
          overlay.setMap(null);
          console.log(`🔥 REMOVED OLD OVERLAY ${index} FOR ${measurementId}`);
        } else if (overlay && overlay.onRemove) {
          overlay.onRemove();
          console.log(`🔥 REMOVED OLD OVERLAY ${index} FOR ${measurementId} via onRemove`);
        }
      });
    });
    
    // Now clear the references
    measurementObjectsRef.current.clear();
    console.log('🔥 OLD OBJECTS REMOVED AND MAP CLEARED - ready for new objects');
    
    // Create objects for all measurements with correct initial visibility
    measurements.forEach(measurement => {
      console.log('📏 Creating objects for measurement:', measurement.id, 'type:', measurement.type, 'visible:', measurement.visible);
      const newObjects = [];
      
      // Phase 2B-Alpha: Handle different measurement types
      if (measurement.type === 'circle') {
        console.log('📏 Creating circle measurement objects:', measurement.id);
        
        // 🔥 CRITICAL DEBUG: Check if map reference is available
        console.log('🔥 CIRCLE DEBUG: googleMapRef.current:', googleMapRef.current ? 'HAS MAP' : 'NO MAP');
        console.log('🔥 CIRCLE DEBUG: Map type:', typeof googleMapRef.current);
        console.log('🔥 CIRCLE DEBUG: Map constructor:', googleMapRef.current ? googleMapRef.current.constructor.name : 'N/A');
        
        if (!googleMapRef.current) {
          console.error('🔥 CIRCLE ERROR: Cannot create circle - no map reference available!');
          return;
        }
        
        // Phase 2C-Alpha: Create circle with custom styling (always with map reference)
        const colors = getCurrentColors();
        console.log('🔥 CIRCLE DEBUG: Creating circle with params:', {
          center: { lat: measurement.center.lat, lng: measurement.center.lng },
          radius: measurement.radius,
          strokeColor: colors.primary,
          strokeOpacity: ringStyleConfig.opacity.primary,
          strokeWeight: ringStyleConfig.strokeWeight.primary,
          fillColor: colors.primary,
          fillOpacity: ringStyleConfig.opacity.fill,
          clickable: false,
          mapRef: googleMapRef.current ? 'HAS MAP' : 'NO MAP'
        });
        
        const circle = new window.google.maps.Circle({
          center: { lat: measurement.center.lat, lng: measurement.center.lng },
          radius: measurement.radius,
          map: googleMapRef.current,
          strokeColor: colors.primary,
          strokeOpacity: ringStyleConfig.opacity.primary,
          strokeWeight: ringStyleConfig.strokeWeight.primary,
          fillColor: colors.primary,
          fillOpacity: ringStyleConfig.opacity.fill,
          clickable: false
        });
        
        console.log('🔥 CIRCLE CREATED: getMap():', circle.getMap() ? 'HAS MAP' : 'NO MAP');
        console.log('🔥 CIRCLE CREATED: Circle center:', circle.getCenter() ? `${circle.getCenter().lat()}, ${circle.getCenter().lng()}` : 'NO CENTER');
        console.log('🔥 CIRCLE CREATED: Circle radius:', circle.getRadius());
        console.log('🔥 CIRCLE CREATED: Circle visible:', circle.getVisible ? circle.getVisible() : 'NO getVisible METHOD');
        console.log('🔥 CIRCLE CREATED: Map center:', googleMapRef.current ? `${googleMapRef.current.getCenter().lat()}, ${googleMapRef.current.getCenter().lng()}` : 'NO MAP CENTER');
        console.log('🔥 CIRCLE CREATED: Map zoom:', googleMapRef.current ? googleMapRef.current.getZoom() : 'NO MAP ZOOM');
        circle.measurementId = measurement.id;
        // 🎯 FIXED: Store circle objects exactly like successful line code
        const circleObjects = [circle]; // Start with main circle
        const circleOverlays = []; // Store overlays separately like line code

        // Phase 2C-Alpha: Create radius rings with custom styling
        if (measurement.radiusRings && measurement.radiusRings.length > 0) {
          measurement.radiusRings.forEach(ringDistance => {
            const ring = new window.google.maps.Circle({
              center: { lat: measurement.center.lat, lng: measurement.center.lng },
              radius: ringDistance,
              map: googleMapRef.current,
              strokeColor: colors.rings,
              strokeOpacity: ringStyleConfig.opacity.rings,
              strokeWeight: ringStyleConfig.strokeWeight.rings,
              fillColor: 'transparent',
              fillOpacity: 0,
              clickable: false
            });
            ring.measurementId = measurement.id;

            // Phase 2C-Alpha: Create zoom-responsive distance label for ring
            const labelOverlay = new window.google.maps.OverlayView();
            labelOverlay.measurementId = measurement.id;
            labelOverlay.onAdd = function() {
              const div = document.createElement('div');
              // Initial styling - will be updated in draw()
              const labelStyle = getZoomResponsiveLabelStyle(googleMapRef.current);
              div.style.cssText = `
                position: absolute;
                background: rgba(255, 255, 255, 0.95);
                border: 1px solid #ddd;
                border-radius: ${labelStyle.borderRadius};
                padding: ${labelStyle.padding};
                font-size: ${labelStyle.fontSize};
                font-weight: ${labelStyle.fontWeight};
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                color: #333;
                pointer-events: none;
                white-space: nowrap;
                box-shadow: 0 2px 4px rgba(0,0,0,0.15);
                letter-spacing: 0.3px;
                transform: translateX(-50%);
              `;
              div.textContent = formatDistance(ringDistance);
              this.div = div;
              const panes = this.getPanes();
              panes.overlayMouseTarget.appendChild(div);
            };

            labelOverlay.draw = function() {
              const overlayProjection = this.getProjection();
              const map = googleMapRef.current;
              
              // Phase 2C-Alpha: Update styling based on current zoom level
              if (this.div && map) {
                const labelStyle = getZoomResponsiveLabelStyle(map);
                this.div.style.fontSize = labelStyle.fontSize;
                this.div.style.padding = labelStyle.padding;
                this.div.style.borderRadius = labelStyle.borderRadius;
                this.div.style.fontWeight = labelStyle.fontWeight;
              }
              
              // Position label at the top of the ring with zoom-aware offset
              const labelPosition = overlayProjection.fromLatLngToDivPixel(
                new window.google.maps.LatLng(
                  measurement.center.lat + (ringDistance / 111320), // Approximate degrees per meter
                  measurement.center.lng
                )
              );
              
              if (this.div && labelPosition && map) {
                const zoom = map.getZoom();
                // Zoom-aware Y offset for better positioning
                const yOffset = zoom >= 15 ? -18 : zoom >= 12 ? -15 : -12;
                
                this.div.style.left = labelPosition.x + 'px';
                this.div.style.top = (labelPosition.y + yOffset) + 'px';
                
                // Zoom-based opacity for better visibility
                const opacity = zoom < 10 ? 0.8 : 0.95;
                this.div.style.background = `rgba(255, 255, 255, ${opacity})`;
              }
            };

            labelOverlay.onRemove = function() {
              if (this.div) {
                this.div.parentNode.removeChild(this.div);
                this.div = null;
              }
            };

            // 🔥 FIXED: Add custom visibility toggle method like line code
            labelOverlay.onToggleVisibility = function(isVisible) {
              if (this.div) {
                this.div.style.display = isVisible ? 'block' : 'none';
                console.log('🔥 CIRCLE LABEL OVERLAY: Set visibility to', isVisible ? 'visible' : 'hidden');
              }
            };

            labelOverlay.setMap(googleMapRef.current);
            
            // Add ring to objects array and label to overlays array (like line code)
            circleObjects.push(ring);
            circleOverlays.push(labelOverlay);
          });
        }

        // GPT-4 SOLUTION: Store in new structured format exactly like working line code
        const circleEntry = {
          type: 'circle',
          objects: circleObjects, // Now includes main circle + all rings
          overlays: circleOverlays // Store overlays separately like line code
        };
        measurementObjectsRef.current.set(measurement.id, circleEntry);
        console.log('🔥 STORED CIRCLE OBJECTS FOR:', measurement.id, 'Total objects:', circleObjects.length, 'Total overlays:', circleOverlays.length);
        
        // Apply visibility after creation using setMap for consistency (like line code)
        if (!measurement.visible) {
          console.log('🔥 SETTING INITIAL VISIBILITY TO FALSE for circle:', measurement.id);
          // Use setMap(null) for objects and custom toggle for overlays (like line code)
          circleObjects.forEach(obj => {
            if (obj && obj.setMap) {
              obj.setMap(null);
            }
          });
          circleOverlays.forEach(overlay => {
            if (overlay && overlay.setMap) {
              overlay.setMap(null);
            }
          });
        } else {
          console.log('🔥 CIRCLE VISIBILITY CHECK: Circle should be visible - measurement.visible:', measurement.visible);
          console.log('🔥 CIRCLE VISIBILITY CHECK: Circle getMap after creation:', circle.getMap() ? 'HAS MAP' : 'NO MAP');
        }
      } else {
        // Handle line measurements
        console.log('📏 Creating line measurement objects:', measurement.id);
        if (!measurement.startPoint || !measurement.endPoint) {
          console.warn('📏 Invalid line measurement data:', measurement);
          return;
        }

        // Create start marker (always with map reference)
        console.log('🔥 CREATING START MARKER - visible:', measurement.visible, 'map: googleMapRef.current');
        const startMarker = new window.google.maps.Marker({
          position: { lat: measurement.startPoint.lat, lng: measurement.startPoint.lng },
          map: googleMapRef.current,
          title: `${measurement.label} - Start`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 6,
            fillColor: '#ff0000',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2
          }
        });
        console.log('🔥 START MARKER CREATED - getMap():', startMarker.getMap() ? 'HAS MAP' : 'NO MAP');
        startMarker.measurementId = measurement.id;

        // Create end marker (always with map reference)
        const endMarker = new window.google.maps.Marker({
          position: { lat: measurement.endPoint.lat, lng: measurement.endPoint.lng },
          map: googleMapRef.current,
          title: `${measurement.label} - End`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 6,
            fillColor: '#ff0000',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2
          }
        });
        endMarker.measurementId = measurement.id;

        // Create line (always with map reference)
        console.log('🔥 CREATING LINE - visible:', measurement.visible, 'map: googleMapRef.current');
        const line = new window.google.maps.Polyline({
          path: [
            { lat: measurement.startPoint.lat, lng: measurement.startPoint.lng },
            { lat: measurement.endPoint.lat, lng: measurement.endPoint.lng }
          ],
          geodesic: true,
          strokeColor: '#ff0000',
          strokeOpacity: 1.0,
          strokeWeight: 3,
          map: googleMapRef.current
        });
        console.log('🔥 LINE CREATED - getMap():', line.getMap() ? 'HAS MAP' : 'NO MAP');
        line.measurementId = measurement.id;

        // Phase B: Create rotated inline text label
        const midLat = (measurement.startPoint.lat + measurement.endPoint.lat) / 2;
        const midLng = (measurement.startPoint.lng + measurement.endPoint.lng) / 2;
        
        const rotationAngle = calculateLineAngle(measurement.startPoint, measurement.endPoint);
        
        const textOverlay = new window.google.maps.OverlayView();
        textOverlay.measurementId = measurement.id;
        textOverlay.onAdd = function() {
          const div = document.createElement('div');
          div.style.cssText = `
            position: absolute;
            background: rgba(255, 255, 255, 0.95);
            border: 1px solid #ccc;
            border-radius: 4px;
            padding: 3px 6px;
            font-size: 12px;
            font-weight: bold;
            font-family: Arial, sans-serif;
            color: #333;
            pointer-events: none;
            white-space: nowrap;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            line-height: 1.2;
            transform-origin: center center;
            display: block;
            visibility: visible;
          `;
          div.textContent = formatDistance(measurement.distance);
          
          this.div = div;
          this.rotationAngle = rotationAngle;
          const panes = this.getPanes();
          panes.overlayMouseTarget.appendChild(div);
          
          console.log('🔥 TEXT OVERLAY onAdd: div created and added to DOM');
        };

        textOverlay.draw = function() {
          const overlayProjection = this.getProjection();
          const position = overlayProjection.fromLatLngToDivPixel(
            new window.google.maps.LatLng(midLat, midLng)
          );
          
          if (this.div && position) {
            const textWidth = this.div.offsetWidth || 50;
            const textHeight = this.div.offsetHeight || 16;
            
            this.div.style.left = (position.x - textWidth / 2) + 'px';
            this.div.style.top = (position.y - textHeight / 2) + 'px';
            this.div.style.transform = `rotate(${this.rotationAngle}deg)`;
          }
        };

        textOverlay.onRemove = function() {
          if (this.div) {
            this.div.parentNode.removeChild(this.div);
            this.div = null;
          }
        };

        // 🔥 CRITICAL FIX: Add custom visibility toggle method for GPT-4 solution
        textOverlay.onToggleVisibility = function(isVisible) {
          if (this.div) {
            this.div.style.display = isVisible ? 'block' : 'none';
            console.log('🔥 TEXT OVERLAY (RECREATED): Set visibility to', isVisible ? 'visible' : 'hidden');
          } else {
            console.log('🔥 TEXT OVERLAY (RECREATED): No div available for visibility toggle');
          }
        };

        textOverlay.setMap(googleMapRef.current);
        console.log('🔥 CREATING TEXT OVERLAY - visible:', measurement.visible, 'map: googleMapRef.current');
        console.log('🔥 TEXT OVERLAY CREATED - getMap():', textOverlay.getMap ? (textOverlay.getMap() ? 'HAS MAP' : 'NO MAP') : 'NO getMap METHOD');
        console.log('🔥 TEXT OVERLAY CREATED - div available:', textOverlay.div ? 'HAS DIV' : 'NO DIV');
        console.log('🔥 TEXT OVERLAY CREATED - measurement ID:', measurement.id, 'expected to be visible:', measurement.visible);
        
        // 🚨 TAG OBJECTS WITH MEASUREMENT ID (SAME AS CIRCLES!)
        startMarker.measurementId = measurement.id;
        endMarker.measurementId = measurement.id;
        line.measurementId = measurement.id;
        textOverlay.measurementId = measurement.id;
        
        // GPT-4 SOLUTION: Store line objects in new structured format
        const lineEntry = {
          type: 'line',
          objects: [startMarker, endMarker, line],
          overlays: [textOverlay]
        };
        measurementObjectsRef.current.set(measurement.id, lineEntry);
        console.log('🔥 STORED LINE OBJECTS FOR:', measurement.id, 'Map size now:', measurementObjectsRef.current.size);
        console.log('🔥 LINE OBJECTS - Start marker map:', startMarker.getMap() ? 'HAS MAP' : 'NO MAP');
        console.log('🔥 LINE OBJECTS - End marker map:', endMarker.getMap() ? 'HAS MAP' : 'NO MAP');
        console.log('🔥 LINE OBJECTS - Line map:', line.getMap() ? 'HAS MAP' : 'NO MAP');
        console.log('🔥 LINE OBJECTS - Text overlay map:', textOverlay.getMap() ? 'HAS MAP' : 'NO MAP');
        
        // Apply visibility after creation using setMap for consistency
        if (!measurement.visible) {
          console.log('🔥 SETTING INITIAL VISIBILITY TO FALSE for:', measurement.id);
          console.log('🔥 LINE RECREATION: Line should be HIDDEN, removing from map');
          // Use setMap(null) instead of setVisible(false) for consistency
          startMarker.setMap(null);
          endMarker.setMap(null);
          line.setMap(null);
          textOverlay.setMap(null);
        } else {
          console.log('🔥 SETTING INITIAL VISIBILITY TO TRUE for:', measurement.id);
          console.log('🔥 LINE RECREATION: Line should be VISIBLE, already on map');
          // CRITICAL FIX: Set text overlay visibility immediately, no setTimeout delays
          // The setTimeout was causing text to disappear when circles were introduced
          console.log('🔥 TEXT OVERLAY: Setting visibility immediately (no delay)');
          // Text overlay is already visible by default when setMap(googleMapRef.current) is called
          // No need for additional visibility calls since it's already on the map
        }
      }
    });

    console.log('🔥 OBJECT CREATION COMPLETE - Final Map size:', measurementObjectsRef.current.size);
    console.log('🔥 OBJECT CREATION COMPLETE - Final Object IDs:', Array.from(measurementObjectsRef.current.keys()));
    console.log('🔥 OBJECT CREATION COMPLETE - Expected:', measurements.map(m => m.id));

    // SIMPLIFIED: Cleanup function without flag system
    return () => {
      console.log('📏 Cleaning up measurement objects');
      console.log('🔥 CLEANUP: Current measurements count:', measurements.length);
      console.log('🔥 CLEANUP: Current object count:', measurementObjectsRef.current.size);
      
      // Only cleanup when measurements are actually being removed
      if (measurements.length === 0) {
        console.log('🔥 CLEANUP: No measurements, cleaning up all objects');
        const objectsToClean = measurementObjectsRef.current;
        
        objectsToClean.forEach((entryGroup, measurementId) => {
          console.log('📏 Cleanup: Removing objects for measurement:', measurementId);
          const { objects = [], overlays = [] } = entryGroup;
          
          objects.forEach(obj => {
            if (obj && obj.setMap) {
              obj.setMap(null);
            }
          });
          
          overlays.forEach(overlay => {
            if (overlay && overlay.setMap) {
              overlay.setMap(null);
            } else if (overlay && overlay.onRemove) {
              overlay.onRemove();
            }
          });
        });
        
        objectsToClean.clear();
        console.log('🔥 CLEANUP: All objects cleared');
      } else {
        console.log('🔥 CLEANUP: Measurements exist, normal cleanup skipped');
      }
    };
  }, [measurements.length, measurements.map(m => m.id).join('|'), unitPreference]); // FIXED: Only run when measurement IDs change, not visibility

  // Phase 2B-Alpha: Circle measurement management functions
  const addCircleMeasurement = (circleMeasurement) => {
    console.log('🔄 MapCore: Adding circle measurement:', circleMeasurement);
    setMeasurements(prev => [...prev, circleMeasurement]);
  };

  const removeCircleMeasurement = (circleId) => {
    console.log('🔄 MapCore: Removing circle measurement:', circleId);
    setMeasurements(prev => prev.filter(m => m.id !== circleId));
  };

  const updateCircleMeasurementVisibility = (circleId, visible) => {
    console.log('🔄 MapCore: Updating circle measurement visibility:', circleId, visible);
    setMeasurements(prev => prev.map(m => 
      m.id === circleId ? { ...m, visible } : m
    ));
  };

  // Create markers for all sites
  const createSiteMarkers = (map, infoWin, sitesToUse = sites, overrideUpdatingStatus = undefined) => {
    console.log('🗺️ Creating markers for sites:', sitesToUse);
    
    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));
    
    const sitesWithCoords = sitesToUse.filter(site => site.latitude && site.longitude);
    console.log('🗺️ Sites with coordinates:', sitesWithCoords.length, 'of', sitesToUse.length);
    
    const newMarkers = sitesWithCoords
      .map(site => {
        console.log('🗺️ Creating marker for site:', site.site_name, 'at', site.latitude, site.longitude);
        const marker = new window.google.maps.Marker({
          position: { lat: site.latitude, lng: site.longitude },
          map: map,
          title: site.site_name,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(createSiteIcon(site))}`,
            scaledSize: new window.google.maps.Size(32, 40),
            anchor: new window.google.maps.Point(16, 40)
          }
        });
        
        // Add click listener for site info
        marker.addListener('click', () => {
          showSiteInfo(site, infoWin, marker, overrideUpdatingStatus);
        });
        
        // Store site data with marker
        marker.siteData = site;
        
        return marker;
      });
    
    console.log('🗺️ Created', newMarkers.length, 'markers');
    setMarkers(newMarkers);
  };

  // Create SVG icon for site marker
  const createSiteIcon = (site) => {
    const typeConfig = SITE_TYPE_CONFIG[site.site_type] || SITE_TYPE_CONFIG.other;
    const statusConfig = SITE_STATUS_CONFIG[site.status] || SITE_STATUS_CONFIG.unknown;
    
    // Debug logging to see what statuses we're getting (temporary)
    // console.log(`🎨 Creating icon for "${site.site_name}":`, {
    //   siteColorMode,
    //   siteType: site.site_type,
    //   siteStatus: site.status,
    //   typeColor: typeConfig.color,
    //   statusColor: statusConfig.color,
    //   willUseColor: siteColorMode === 'status' ? statusConfig.color : typeConfig.color
    // });
    
    // Use siteColorMode to determine primary color source
    const colorConfig = siteColorMode === 'status' ? statusConfig : typeConfig;
    const color = colorConfig.color;
    const letter = typeConfig.letter; // Always use type letter for identification
    const isSelected = selectedSites.some(s => s.id === site.id);
    
    // Use selection color for stroke when selected, otherwise use subtle stroke
    const strokeColor = isSelected ? '#000' : '#ffffff';
    const strokeWidth = isSelected ? 4 : 2;
    
    return `
      <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 0C7.2 0 0 7.2 0 16c0 8.8 16 24 16 24s16-15.2 16-24C32 7.2 24.8 0 16 0z" 
              fill="${color}" 
              stroke="${strokeColor}" 
              stroke-width="${strokeWidth}"/>
        <circle cx="16" cy="16" r="6" fill="white"/>
        <text x="16" y="20" text-anchor="middle" font-family="Arial" font-size="8" font-weight="bold" fill="${color}">
          ${letter}
        </text>
        ${site.status !== 'active' && site.status !== 'unknown' ? `
          <circle cx="26" cy="6" r="4" fill="${statusConfig.color}" stroke="white" stroke-width="1"/>
        ` : ''}
      </svg>
    `;
  };

  // SMS Templates for individual sites
  const INDIVIDUAL_SMS_TEMPLATES = [
    {
      id: 'site_safety_check',
      name: 'Site Safety Check',
      message: 'This is a site security/safety check from {organisation_name} Crisis Management. Please confirm your site\'s operational status:\n1 = Active\n2 = Degraded\n3 = Evacuating\n4 = Evacuated\n5 = Closed and Secure'
    },
    {
      id: 'site_emergency_alert',
      name: 'Emergency Alert',
      message: 'EMERGENCY ALERT: This is an urgent notification regarding your site. Please respond immediately to confirm your safety and site status.'
    },
    {
      id: 'site_evacuation_notice',
      name: 'Evacuation Notice',
      message: 'EVACUATION NOTICE: Please evacuate your site immediately and proceed to the designated safe area. Confirm evacuation completion by replying to this message.'
    }
  ];

  // Handle individual site SMS
  const handleIndividualSiteSMS = (site) => {
    console.log('💬 Opening individual SMS modal for site:', site.site_name);
    setSelectedSiteForSMS(site);
    setShowIndividualSMSModal(true);
    individualSMSForm.resetFields();
    
    // Close the InfoWindow
    if (infoWindow) {
      infoWindow.close();
    }
  };

  // Send individual SMS
  const sendIndividualSMS = async (values) => {
    setSendingIndividualSMS(true);
    
    try {
      const { message: smsMessage } = values;
      
      if (!smsMessage || !smsMessage.trim()) {
        message.error('Please enter a message to send');
        return;
      }

      if (!selectedSiteForSMS?.site_manager_mobile?.trim()) {
        message.error('No valid mobile number found for this site manager');
        return;
      }

      // Extract incidentId from URL for SMS tracking
      const urlParams = new URLSearchParams(window.location.search);
      const incidentId = urlParams.get('id') || window.location.pathname.split('/').pop();

      // Replace {organisation_name} template variable
      let processedMessage = smsMessage;
      if (smsMessage.includes('{organisation_name}')) {
        try {
          // Fetch organization data for name replacement
          const { getDoc, doc } = await import('firebase/firestore');
          const { db } = await import('../../config/firebase');
          const orgRef = doc(db, 'c3_organisations', currentUser.orgId);
          const orgDoc = await getDoc(orgRef);
          
          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            const orgName = orgData.name || orgData.companyName || 'Your Organization';
            processedMessage = smsMessage.replace(/{organisation_name}/g, orgName);
            console.log(`🏢 Replaced organization name: ${orgName}`);
          }
        } catch (error) {
          console.warn('⚠️ Could not fetch organization name for template replacement:', error);
          processedMessage = smsMessage.replace(/{organisation_name}/g, 'Your Organization');
        }
      }

      console.log(`💬 Sending individual SMS to ${selectedSiteForSMS.site_manager_name} at ${selectedSiteForSMS.site_manager_mobile}`);
      
      const response = await fetch('https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/sendSMS', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: selectedSiteForSMS.site_manager_mobile,
          message: processedMessage,
          orgId: currentUser.orgId,
          userId: currentUser.uid,
          incidentId: (incidentId && incidentId !== 'map') ? incidentId : null // NEW: Include incident ID
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        message.success(`SMS sent successfully to ${selectedSiteForSMS.site_manager_name}`);
        console.log(`✅ Individual SMS sent successfully to ${selectedSiteForSMS.site_manager_name}`);
        
        // PHASE 2: Log SMS activity to incident logs
        await logSMSActivity({
          type: 'individual',
          recipient: {
            name: selectedSiteForSMS.site_manager_name,
            phone: selectedSiteForSMS.site_manager_mobile,
            site: selectedSiteForSMS.site_name
          },
          message: smsMessage,
          status: 'success',
          sid: result.sid
        });
        
        // Clear form and close modal
        individualSMSForm.resetFields();
        setShowIndividualSMSModal(false);
        setSelectedSiteForSMS(null);
      } else {
        message.error(`Failed to send SMS: ${result.error || 'Unknown error'}`);
        console.error(`❌ Individual SMS failed for ${selectedSiteForSMS.site_manager_name}:`, result.error);
        
        // Log failed SMS attempt
        await logSMSActivity({
          type: 'individual',
          recipient: {
            name: selectedSiteForSMS.site_manager_name,
            phone: selectedSiteForSMS.site_manager_mobile,
            site: selectedSiteForSMS.site_name
          },
          message: smsMessage,
          status: 'failed',
          error: result.error || 'Unknown error'
        });
      }

    } catch (error) {
      console.error('❌ Individual SMS error:', error);
      message.error('Failed to send SMS: ' + error.message);
      
      // Log error
      if (selectedSiteForSMS) {
        await logSMSActivity({
          type: 'individual',
          recipient: {
            name: selectedSiteForSMS.site_manager_name,
            phone: selectedSiteForSMS.site_manager_mobile,
            site: selectedSiteForSMS.site_name
          },
          message: values.message || '',
          status: 'error',
          error: error.message
        });
      }
    } finally {
      setSendingIndividualSMS(false);
    }
  };

  // Apply SMS template for individual SMS
  const applyIndividualTemplate = async (template) => {
    let processedMessage = template.message;
    
    // Replace organization name in template display
    if (template.message.includes('{organisation_name}')) {
      try {
        const { getDoc, doc } = await import('firebase/firestore');
        const { db } = await import('../../config/firebase');
        const orgRef = doc(db, 'c3_organisations', currentUser.orgId);
        const orgDoc = await getDoc(orgRef);
        
        if (orgDoc.exists()) {
          const orgData = orgDoc.data();
          const orgName = orgData.name || orgData.companyName || 'Your Organization';
          processedMessage = template.message.replace(/{organisation_name}/g, orgName);
        }
      } catch (error) {
        console.warn('⚠️ Could not fetch organization name for template display:', error);
        processedMessage = template.message.replace(/{organisation_name}/g, 'Your Organization');
      }
    }
    
    individualSMSForm.setFieldsValue({ message: processedMessage });
  };

  // Close individual SMS modal
  const closeIndividualSMSModal = () => {
    setShowIndividualSMSModal(false);
    setSelectedSiteForSMS(null);
    individualSMSForm.resetFields();
  };

  // Handle site status update
  const handleSiteStatusUpdate = async (siteId, newStatus) => {
    // Prevent multiple simultaneous updates for the same site
    if (updatingSiteStatus === siteId) {
      console.log('🔒 Status update already in progress for site:', siteId);
      return;
    }
    
    // Set a timeout to ensure we don't get stuck
    const timeoutId = setTimeout(() => {
      console.warn('⏰ Status update timeout - forcing reset');
      setUpdatingSiteStatus(null);
    }, 10000); // 10 second timeout
    
    try {
      console.log('🔄 Starting status update for site:', siteId, 'to:', newStatus);
      setUpdatingSiteStatus(siteId);
      
      // Find the current site and marker
      const currentSite = sites.find(s => s.id === siteId);
      const currentMarker = markers.find(m => m.siteData && m.siteData.id === siteId);
      
      // Optimistically update the UI
      const updatedSites = sites.map(site => 
        site.id === siteId ? { ...site, status: newStatus } : site
      );
      console.log('🔥 MapCore: About to setSites with updated status!', {
        siteId,
        newStatus,
        originalSites: sites.length,
        updatedSites: updatedSites.length,
        changedSite: updatedSites.find(s => s.id === siteId)
      });
      setSites(updatedSites);
      console.log('🔥 MapCore: setSites called - this should trigger onSitesLoaded useEffect!');
      
      // Update backend first
      console.log('📡 Updating backend...');
      await updateSiteStatus(siteId, newStatus, currentUser.uid);
      console.log('✅ Backend update successful');
      
      // Log the status change (non-critical, don't fail if this errors)
      try {
        await logSiteStatusChange(siteId, newStatus);
        console.log('📝 Status change logged');
      } catch (logError) {
        console.warn('⚠️ Failed to log status change (non-critical):', logError);
      }
      
      // Clear updating status
      console.log('🔓 Clearing updating status');
      setUpdatingSiteStatus(null);
      
      // Update only the specific marker that changed
      if (currentMarker && currentSite) {
        const updatedSite = { ...currentSite, status: newStatus };
        
        // Update the marker's site data
        currentMarker.siteData = updatedSite;
        
        // Update the marker's icon to reflect new status
        currentMarker.setIcon({
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(createSiteIcon(updatedSite))}`,
          scaledSize: new window.google.maps.Size(32, 40),
          anchor: new window.google.maps.Point(16, 40)
        });
        
        // Update the info window content if it's currently open for this site
        if (infoWindow && infoWindow.getContent() && infoWindow.getContent().includes(siteId)) {
          showSiteInfo(updatedSite, infoWindow, currentMarker, 'none');
        }
      }
      
      message.success(`Site status updated to ${SITE_STATUS_CONFIG[newStatus]?.label || newStatus}`);
      console.log('✅ Status update completed successfully');
      
    } catch (error) {
      console.error('❌ Error updating site status:', error);
      message.error('Failed to update site status');
      
      // Always clear updating status on error
      setUpdatingSiteStatus(null);
      
      // Revert optimistic update on error
      try {
        await loadSites();
      } catch (loadError) {
        console.error('❌ Failed to reload sites after error:', loadError);
      }
    } finally {
      // Always clear the timeout
      clearTimeout(timeoutId);
      console.log('🧹 Cleanup completed');
    }
  };

  // Log site status change for audit trail  
  const logSiteStatusChange = async (siteId, newStatus) => {
    try {
      const site = sites.find(s => s.id === siteId);
      if (!site) return;
      
      // ALWAYS log site status changes for dashboard updates
      await createIncidentLog({
        incidentId: null, // Allow non-incident logging for dashboard tracking
        orgId: currentUser.orgId,
        action: 'site_status_update',
        details: `Site "${site.site_name}" status changed to ${SITE_STATUS_CONFIG[newStatus]?.label || newStatus}`,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userFullName: currentUser.displayName || currentUser.email,
        priority: 'normal',
        metadata: {
          siteId: siteId,
          siteName: site.site_name,
          oldStatus: site.status,
          newStatus: newStatus,
          siteType: site.site_type,
          address: site.address_line_1,
          context: 'map_interface' // Track where change was made
        }
      });

      console.log('📊 Site status change logged for dashboard real-time update');
    } catch (error) {
      console.error('Error logging site status change:', error);
      // Don't throw - logging failure shouldn't break status update
    }
  };

  // PHASE 2: Log SMS activity to incident logs
  const logSMSActivity = async ({ type, recipient, message: smsMessage, status, sid, error }) => {
    try {
      // Only log if we're in a crisis context (check for incidentId in URL or props)
      const urlParams = new URLSearchParams(window.location.search);
      const incidentId = urlParams.get('id') || window.location.pathname.split('/').pop();
      
      if (!incidentId || incidentId === 'map') {
        console.log('💬 SMS sent outside incident context - not logging to incident logs');
        return;
      }

      const details = status === 'success' 
        ? `SMS sent to ${recipient.name} (${recipient.site}) at ${recipient.phone}. Message: "${smsMessage.substring(0, 100)}${smsMessage.length > 100 ? '...' : ''}"`
        : `SMS failed to ${recipient.name} (${recipient.site}) at ${recipient.phone}. Error: ${error}. Message: "${smsMessage.substring(0, 100)}${smsMessage.length > 100 ? '...' : ''}"`;

      await createIncidentLog({
        incidentId: incidentId,
        orgId: currentUser.orgId,
        action: 'sms_communication',
        details: details,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userFullName: currentUser.displayName || currentUser.email,
        priority: status === 'success' ? 'normal' : 'high',
        metadata: {
          smsType: type,
          recipientName: recipient.name,
          recipientPhone: recipient.phone,
          siteName: recipient.site,
          messageLength: smsMessage.length,
          status: status,
          twilioSid: sid || null,
          errorMessage: error || null,
          communicationType: 'sms'
        }
      });

      console.log(`📋 SMS activity logged to incident ${incidentId}: ${status} - ${recipient.name}`);
    } catch (logError) {
      console.error('❌ Failed to log SMS activity:', logError);
      // Don't throw - SMS logging failure shouldn't break SMS functionality
    }
  };

  // Show site information in popup
  const showSiteInfo = (site, infoWin, marker, overrideUpdatingStatus = undefined) => {
    // Create unique button IDs for this site
    const smsButtonId = `sms-btn-${site.id}`;
    const statusSelectId = `status-select-${site.id}`;
    
    const statusConfig = SITE_STATUS_CONFIG[site.status] || SITE_STATUS_CONFIG.unknown;
    const isUpdating = overrideUpdatingStatus !== undefined ? 
      (overrideUpdatingStatus === 'none' ? false : overrideUpdatingStatus === site.id) : 
      updatingSiteStatus === site.id;
    
    const content = `
      <div style="max-width: 280px; font-family: Arial, sans-serif;">
        <h3 style="margin: 0 0 8px 0; color: #39325b;">${site.site_name}</h3>
        <div style="margin-bottom: 6px;">
          <strong>Type:</strong> ${site.site_type.charAt(0).toUpperCase() + site.site_type.slice(1)}
        </div>
        <div style="margin-bottom: 6px;">
          <strong>Address:</strong><br/>
          ${site.address_line_1}${site.address_line_2 ? '<br/>' + site.address_line_2 : ''}<br/>
          ${site.suburb}, ${site.state} ${site.postcode}
        </div>
        ${site.region ? `<div style="margin-bottom: 6px;"><strong>Region:</strong> ${site.region}</div>` : ''}
        <div style="margin-bottom: 8px;">
          <strong>Status:</strong><br/>
          <div style="margin-top: 4px;">
            <select 
              id="${statusSelectId}"
              style="
                width: 100%; 
                padding: 4px 8px; 
                border: 1px solid #ddd; 
                border-radius: 4px; 
                font-size: 13px;
                background: ${isUpdating ? '#f5f5f5' : 'white'};
                color: ${statusConfig.color};
                font-weight: bold;
              "
              ${isUpdating ? 'disabled' : ''}
            >
              ${Object.entries(SITE_STATUS_CONFIG).map(([key, config]) => 
                `<option value="${key}" ${site.status === key ? 'selected' : ''} style="color: ${config.color};">
                  ${config.label}
                </option>`
              ).join('')}
            </select>
            ${isUpdating ? `
              <div style="margin-top: 4px; font-size: 12px; color: #666;">
                ⏳ Updating status...
              </div>
            ` : ''}
          </div>
        </div>
        <div style="border-top: 1px solid #eee; padding-top: 8px; margin-top: 8px;">
          <div style="margin-bottom: 4px;">
            <strong>Site Manager:</strong> ${site.site_manager_name}
          </div>
          <div style="margin-bottom: 8px; display: flex; align-items: center; gap: 8px;">
            <a href="tel:${site.site_manager_mobile}" style="color: #27ae60; text-decoration: none; display: flex; align-items: center; gap: 4px;">
              📞 ${site.site_manager_mobile}
            </a>
            ${site.site_manager_mobile && site.site_manager_mobile.trim() ? `
              <button 
                id="${smsButtonId}"
                style="
                  background: #39325b; 
                  color: white; 
                  border: none; 
                  border-radius: 4px; 
                  padding: 4px 8px; 
                  cursor: pointer; 
                  font-size: 12px;
                  display: flex;
                  align-items: center;
                  gap: 4px;
                "
                title="Send SMS to ${site.site_manager_name}"
              >
                💬 SMS
              </button>
            ` : ''}
          </div>
          ${site.site_manager_email ? `
            <div style="margin-bottom: 4px;">
              <a href="mailto:${site.site_manager_email}" style="color: #3498db; text-decoration: none; display: flex; align-items: center; gap: 4px;">
                ✉️ ${site.site_manager_email}
              </a>
            </div>
          ` : ''}
        </div>
      </div>
    `;
    
    infoWin.setContent(content);
    infoWin.open(googleMapRef.current, marker);
    
    // Add event listeners after InfoWindow is rendered
    setTimeout(() => {
      // SMS button listener
      const smsButton = document.getElementById(smsButtonId);
      if (smsButton && site.site_manager_mobile && site.site_manager_mobile.trim()) {
        smsButton.addEventListener('click', () => {
          handleIndividualSiteSMS(site);
        });
      }
      
      // Status dropdown listener
      const statusSelect = document.getElementById(statusSelectId);
      if (statusSelect) {
        statusSelect.addEventListener('change', (e) => {
          const newStatus = e.target.value;
          if (newStatus !== site.status) {
            handleSiteStatusUpdate(site.id, newStatus);
          }
        });
      }
    }, 100);
  };

  // 🆕 PHASE 4: Add draggable crisis location marker with manual override capability
  const addCrisisLocationMarker = (map, location) => {
    if (location.latitude && location.longitude) {
      const crisisMarker = new window.google.maps.Marker({
        position: { lat: location.latitude, lng: location.longitude },
        map: map,
        title: `Crisis Location: ${location.title || 'Unknown'} (Drag to reposition)`,
        draggable: true, // 🎯 PHASE 4: Enable dragging
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 0C7.2 0 0 7.2 0 16c0 8.8 16 24 16 24s16-15.2 16-24C32 7.2 24.8 0 16 0z" 
                    fill="#ff4444" stroke="#000" stroke-width="2"/>
              <text x="16" y="20" text-anchor="middle" font-family="Arial" font-size="12" fill="white">!</text>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(32, 40),
          anchor: new window.google.maps.Point(16, 40)
        },
        zIndex: 1000 // Ensure crisis marker appears above other markers
      });

      // Store reference to crisis marker
      setCrisisLocationMarker(crisisMarker);

      // 🎯 PHASE 4: Add drag event listeners for manual positioning
      crisisMarker.addListener('dragstart', (event) => {
        console.log('🎯 PHASE 4: Crisis location drag started');
        setIsDraggingCrisisLocation(true);
        setDragStartPosition({
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        });
        
        // Close info window during drag
        if (infoWindow) {
          infoWindow.close();
        }
      });

      crisisMarker.addListener('drag', (event) => {
        // Visual feedback during dragging (optional - marker itself moves)
        console.log('🎯 PHASE 4: Crisis location being dragged to:', {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        });
      });

      crisisMarker.addListener('dragend', (event) => {
        console.log('🎯 PHASE 4: Crisis location drag ended');
        const newPosition = {
          latitude: event.latLng.lat(),
          longitude: event.latLng.lng()
        };
        
        setIsDraggingCrisisLocation(false);
        setDragStartPosition(null);
        
        // 🎯 PHASE 4: Call callback to update incident data
        if (onCrisisLocationDrag) {
          console.log('🎯 PHASE 4: Notifying parent of new crisis location:', newPosition);
          onCrisisLocationDrag(newPosition);
        }
        
        // Show updated info window with new coordinates
        showCrisisLocationInfo({
          ...location,
          latitude: newPosition.latitude,
          longitude: newPosition.longitude,
          manuallyPositioned: true,
          repositionedAt: new Date()
        }, crisisMarker);
      });

      // Add click listener for crisis location info (only when not dragging)
      crisisMarker.addListener('click', () => {
        if (!isDraggingCrisisLocation) {
          showCrisisLocationInfo(location, crisisMarker);
        }
      });

      return crisisMarker;
    }
    return null;
  };

  // 🔄 PHASE 4: Enhanced crisis location info window with manual positioning feedback
  const showCrisisLocationInfo = (location, marker) => {
    if (!infoWindow) return;
    
    const contentString = `
      <div style="max-width: 320px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
        <h3 style="margin: 0 0 12px 0; color: #ff4444; font-size: 16px; font-weight: 600;">
          🎯 Crisis Epicenter ${location.manuallyPositioned ? '(Manually Positioned)' : ''}
        </h3>
        <div style="margin-bottom: 8px;">
          <strong>Location:</strong> ${location.address || location.title || 'Unknown'}
        </div>
        ${location.reverseGeocodeName ? `
          <div style="margin-bottom: 8px;">
            <strong>Area:</strong> ${location.reverseGeocodeName}
          </div>
        ` : ''}
        <div style="margin-bottom: 8px;">
          <strong>Coordinates:</strong> ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}
        </div>
        ${location.formattedAddress ? `
          <div style="margin-bottom: 8px;">
            <strong>Formatted Address:</strong> ${location.formattedAddress}
          </div>
        ` : ''}
        ${location.geocodedAt ? `
          <div style="margin-bottom: 8px; font-size: 12px; color: #666;">
            <strong>Geocoded:</strong> ${new Date(location.geocodedAt).toLocaleString()}
          </div>
        ` : ''}
        ${location.manuallyPositioned && location.repositionedAt ? `
          <div style="margin-bottom: 8px; font-size: 12px; color: #ff4444; background: #fff5f5; padding: 6px; border-radius: 4px; border: 1px solid #ffebee;">
            <strong>🎯 Manual Override:</strong> Position manually adjusted at ${new Date(location.repositionedAt).toLocaleString()}
          </div>
        ` : ''}
        <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #eee; font-size: 12px; color: #666;">
          ${location.manuallyPositioned 
            ? 'This marker has been manually positioned to override automatic geocoding. Click and drag to reposition again.' 
            : 'This marker indicates the primary location of the crisis incident. Click and drag to manually reposition if needed.'
          }
        </div>
      </div>
    `;
    
    infoWindow.setContent(contentString);
    infoWindow.open(googleMapRef.current, marker);
  };

  // Get bounds from drawn shape
  const getShapeBounds = (shape, type) => {
    let bounds;
    
    if (type === 'rectangle') {
      bounds = shape.getBounds();
    } else if (type === 'polygon') {
      bounds = new window.google.maps.LatLngBounds();
      const path = shape.getPath();
      for (let i = 0; i < path.getLength(); i++) {
        bounds.extend(path.getAt(i));
      }
    }
    
    if (bounds) {
      return {
        north: bounds.getNorthEast().lat(),
        south: bounds.getSouthWest().lat(),
        east: bounds.getNorthEast().lng(),
        west: bounds.getSouthWest().lng()
      };
    }
    
    return null;
  };

  // Select sites within drawn bounds
  const selectSitesInBounds = (bounds) => {
    const sitesInBounds = sites.filter(site => {
      if (!site.latitude || !site.longitude) return false;
      
      return (
        site.latitude >= bounds.south &&
        site.latitude <= bounds.north &&
        site.longitude >= bounds.west &&
        site.longitude <= bounds.east
      );
    });
    
    setSelectedSites(sitesInBounds);
    
    // Update marker styles
    markers.forEach(marker => {
      const isSelected = sitesInBounds.some(site => site.id === marker.siteData.id);
      if (isSelected) {
        marker.setIcon({
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(createSiteIcon(marker.siteData))}`,
          scaledSize: new window.google.maps.Size(32, 40),
          anchor: new window.google.maps.Point(16, 40)
        });
      }
    });
    
    // Notify parent component
    if (onSitesSelected) {
      onSitesSelected(sitesInBounds);
    }
  };

  // PHASE 1: Detect sites within a circle using actual geometric distance
  const detectSitesInCircle = (circle) => {
    if (!circle || !sites || sites.length === 0) {
      console.log('🎯 PHASE1: No circle or sites to detect');
      return [];
    }

    const center = circle.getCenter();
    const radiusMeters = circle.getRadius();
    
    console.log('🎯 PHASE1: Detecting sites in circle:', {
      center: { lat: center.lat(), lng: center.lng() },
      radiusMeters: radiusMeters,
      totalSites: sites.length
    });

    // 🔍 DEBUG: Check for duplicate sites in source data
    const siteNames = sites.map(site => site.site_name);
    const uniqueSiteNames = new Set(siteNames);
    if (siteNames.length !== uniqueSiteNames.size) {
      console.log('🚨 DUPLICATE SITES DETECTED in source data:');
      const duplicates = siteNames.filter((name, index) => siteNames.indexOf(name) !== index);
      console.log('🚨 Duplicate site names:', [...new Set(duplicates)]);
      
      // Show detailed duplicate analysis
      duplicates.forEach(dupName => {
        const duplicateSites = sites.filter(site => site.site_name === dupName);
        console.log(`🚨 "${dupName}" appears ${duplicateSites.length} times:`, duplicateSites);
      });
    }

    const sitesInCircle = sites.filter(site => {
      if (!site.latitude || !site.longitude) {
        console.log('🎯 PHASE1: Skipping site without coordinates:', site.site_name);
        return false;
      }

      // Calculate distance using Haversine formula
      const distance = calculateHaversineDistance(
        center.lat(), center.lng(),
        site.latitude, site.longitude
      );

      const isInside = distance <= radiusMeters;
      
      if (isInside) {
        console.log('🎯 PHASE1: Site INSIDE circle:', {
          id: site.id,
          name: site.site_name,
          distance: distance.toFixed(2) + 'm',
          radius: radiusMeters.toFixed(2) + 'm',
          coordinates: { lat: site.latitude, lng: site.longitude }
        });
      }

      return isInside;
    });

    // 🔍 DEBUG: Check for duplicates in filtered results
    const detectedNames = sitesInCircle.map(site => site.site_name);
    const uniqueDetectedNames = new Set(detectedNames);
    if (detectedNames.length !== uniqueDetectedNames.size) {
      console.log('🚨 DUPLICATE SITES in filtered results:');
      const filteredDuplicates = detectedNames.filter((name, index) => detectedNames.indexOf(name) !== index);
      console.log('🚨 Filtered duplicate names:', [...new Set(filteredDuplicates)]);
    }

    console.log('🎯 PHASE1: Sites detected in circle:', sitesInCircle.length);
    console.log('🎯 PHASE1: Unique site names detected:', uniqueDetectedNames.size);
    return sitesInCircle;
  };

  // PHASE 1: Haversine distance calculation (accurate for geographical distances)
  const calculateHaversineDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in meters
  };

  // Start drawing mode
  const startDrawing = (mode) => {
    if (drawingManager) {
      setIsDrawing(true);
      drawingManager.setDrawingMode(mode);
    }
  };

  // PHASE 1: Handle circle completion and trigger site detection
  const handleCircleComplete = (circle) => {
    console.log('🔬 MapCore: handleCircleComplete called - detecting sites...');
    console.log('🔬 MapCore: Circle object received:', circle);
    console.log('🔬 MapCore: Available sites for detection:', sites.length);
    
    const detectedSites = detectSitesInCircle(circle);
    
    console.log('🔬 MapCore: Sites detected in circle:', detectedSites.length);
    console.log('🔬 MapCore: Detected sites:', detectedSites);
    
    setCircleDetectedSites(detectedSites);
    setActiveCircle(circle);
    setShowSitePopup(true);
    
    // 🎯 DASHBOARD FIX: Notify Dashboard about popup state change
    if (onSitePopupStateChange) {
      onSitePopupStateChange(true);
    }
    if (onSitePopupDataChange) {
      onSitePopupDataChange({
        sites: detectedSites,
        circle: circle,
        onClose: handleCloseSitePopup
      });
    }
    
    console.log('🔬 MapCore: Site detection complete:', {
      detectedCount: detectedSites.length,
      showPopup: true,
      popupShouldShow: detectedSites.length > 0
    });
  };

  // PHASE 1: Handle closing site popup
  const handleCloseSitePopup = () => {
    setShowSitePopup(false);
    setCircleDetectedSites([]);
    setActiveCircle(null);
    
    // 🎯 DASHBOARD FIX: Notify Dashboard about popup closing
    if (onSitePopupStateChange) {
      onSitePopupStateChange(false);
    }
    if (onSitePopupDataChange) {
      onSitePopupDataChange(null);
    }
  };

  // Measurement tool functions (centralized in MapCore)
  const startMeasurement = () => {
    console.log('🗺️ Starting measurement mode');
    // Phase 1B: Only clear the current temporary measurement, not saved ones
    clearCurrentMeasurement();
    
    setMeasurementMode('first-click');
    
    // Disable drawing manager when measuring
    if (drawingManager) {
      drawingManager.setDrawingMode(null);
    }
    setIsDrawing(false);
  };

  // Phase C-Alpha: Unit-aware distance formatting helper
  const formatDistance = (distanceMeters, unit = unitPreference) => {
    // Handle invalid distance values
    if (isNaN(distanceMeters) || distanceMeters === null || distanceMeters === undefined || distanceMeters < 0) {
      console.error('❌ formatDistance received invalid value:', distanceMeters);
      return 'Error';
    }
    
    if (unit === 'mi') {
      const distanceMiles = (distanceMeters / 1609.34).toFixed(2);
      return `${distanceMiles} mi`;
    } else {
      const distanceKm = (distanceMeters / 1000).toFixed(2);
      return `${distanceKm} km`;
    }
  };

  // Phase B: Calculate angle between two geographic points for text rotation
  const calculateLineAngle = (startPoint, endPoint, overlayProjection = null) => {
    // Simple but effective approach: use Mercator projection approximation
    // This works well for most zoom levels and geographic regions
    
    // For screen coordinates, we need to account for the fact that:
    // - Longitude differences translate directly to X differences
    // - Latitude differences need to be inverted for screen Y coordinates
    const deltaLng = endPoint.lng - startPoint.lng;
    const deltaLat = endPoint.lat - startPoint.lat;
    
    // Calculate angle using standard screen coordinate system
    // Note: we invert deltaLat because screen Y increases downward
    const angleRadians = Math.atan2(-deltaLat, deltaLng);
    let angleDegrees = angleRadians * (180 / Math.PI);
    
    // Debug: Log calculation details
    console.log('📐 Angle calculation:', {
      startPoint,
      endPoint,
      deltaLng,
      deltaLat,
      angleRadians,
      angleDegrees: angleDegrees,
      normalized: false
    });
    
    // Normalize angle to keep text readable (avoid upside-down text)
    // If angle is between 90° and 270°, flip it by 180° to keep text right-side up
    if (angleDegrees > 90 && angleDegrees <= 270) {
      angleDegrees -= 180;
      console.log('📐 Normalized angle (flipped):', angleDegrees);
    } else if (angleDegrees < -90 && angleDegrees >= -270) {
      angleDegrees += 180;
      console.log('📐 Normalized angle (flipped):', angleDegrees);
    }
    
    return angleDegrees;
  };

  // Phase 1B: Clear only the current temporary measurement (not saved ones)
  const clearCurrentMeasurement = () => {
    console.log('🗺️ Clearing current temporary measurement');
    // Remove temporary markers
    measurementMarkers.forEach(marker => marker.setMap(null));
    setMeasurementMarkers([]);
    
    // Remove temporary line
    if (measurementLine) {
      measurementLine.setMap(null);
      setMeasurementLine(null);
    }
    
    // Remove temporary text overlay
    if (measurementInfoWindow) {
      if (measurementInfoWindow.close) {
        // Legacy InfoWindow support
        measurementInfoWindow.close();
      } else if (measurementInfoWindow.setMap) {
        // New textOverlay support
        measurementInfoWindow.setMap(null);
      }
      setMeasurementInfoWindow(null);
    }
    
    setMeasurementData(null);
    setMeasurementMode('idle');
  };

  // Legacy function for backwards compatibility (now same as clearCurrentMeasurement)
  const clearMeasurement = () => {
    console.log('📏 clearMeasurement called');
    clearCurrentMeasurement();
  };

  // Phase 1B: Clear all measurements
  const clearAllMeasurements = () => {
    console.log('🔥 MAPCORE: clearAllMeasurements called');
    console.log('🔥 MAPCORE: Current measurements before clear:', measurements);
    setMeasurements([]);
    setMeasurementIdCounter(1);
    
    // CRITICAL FIX: Explicitly clear localStorage to prevent resurrection on refresh
    // Use the same key as saveMeasurementsToStorage function
    console.log('🔥 MAPCORE: Explicitly clearing localStorage');
    localStorage.removeItem('crisisCompass_measurements');
    
    console.log('🔥 MAPCORE: clearAllMeasurements completed');
  };

  // Phase 1B: Delete specific measurement
  // GPT-4 SOLUTION: Updated delete function
  const deleteMeasurement = (measurementId) => {
    console.log('🔥 UNIFIED DELETE called with ID:', measurementId);
    console.log('🔥 UNIFIED DELETE: Current measurements before delete:', measurements);
    
    // FIRST: Remove the Google Maps objects from the map using new structure
    const measurementEntry = measurementObjectsRef.current.get(measurementId);
    if (measurementEntry) {
      console.log('🔥 UNIFIED DELETE: Removing Google Maps objects for measurement:', measurementId);
      
      const { objects = [], overlays = [] } = measurementEntry;
      
      // Remove all Google Maps objects
      objects.forEach((obj, index) => {
        if (obj && obj.setMap) {
          obj.setMap(null);
          console.log(`🔥 UNIFIED DELETE: Removed object ${index} from map using setMap(null)`);
        } else if (obj && obj.close) {
          obj.close();
          console.log(`🔥 UNIFIED DELETE: Closed object ${index} using close()`);
        } else if (obj && obj.onRemove) {
          obj.onRemove();
          console.log(`🔥 UNIFIED DELETE: Removed object ${index} using onRemove()`);
        }
      });
      
      // Remove all custom overlays
      overlays.forEach((overlay, index) => {
        if (overlay && overlay.setMap) {
          overlay.setMap(null);
          console.log(`🔥 UNIFIED DELETE: Removed overlay ${index} from map using setMap(null)`);
        } else if (overlay && overlay.onRemove) {
          overlay.onRemove();
          console.log(`🔥 UNIFIED DELETE: Removed overlay ${index} using onRemove()`);
        }
      });
      
      // Remove from the objects map
      measurementObjectsRef.current.delete(measurementId);
      console.log('🔥 UNIFIED DELETE: Removed measurement from objects map');
    } else {
      console.warn('🔥 UNIFIED DELETE: No objects found for measurement:', measurementId);
    }
    
    // SECOND: Remove from the state array
    setMeasurements(prev => {
      const filtered = prev.filter(m => m.id !== measurementId);
      console.log('🔥 UNIFIED DELETE: Measurements after filter:', filtered);
      return filtered;
    });
    console.log('🔥 UNIFIED DELETE: deleteMeasurement completed');
  };

  const toggleMeasurementVisibility = (measurementId) => {
    console.log('🔥 MAPCORE: toggleMeasurementVisibility called with ID:', measurementId);
    
    // CRITICAL FIX: Set flag to prevent useEffect interference
    isDirectToggleRef.current = true;
    console.log('🔥 MAPCORE: Flag set to prevent useEffect interference');
    
    // Update state AND directly manipulate Google Maps objects
    setMeasurements(prev => {
      const updated = prev.map(measurement => {
        if (measurement.id === measurementId) {
          const newVisibility = !measurement.visible;
          console.log('🔥 MAPCORE: Toggling measurement:', measurement.id, 'from', measurement.visible, 'to', newVisibility);
          
          // Get the objects for this specific measurement from the Map (using new structure)
          const measurementEntry = measurementObjectsRef.current.get(measurementId);
          if (measurementEntry) {
            console.log('🔥 MAPCORE: Found objects for measurement:', measurementId);
            
            const { objects = [], overlays = [] } = measurementEntry;
            
            // Handle all objects
            objects.forEach(obj => {
              if (obj && obj.setMap) {
                const targetMap = newVisibility ? googleMapRef.current : null;
                obj.setMap(targetMap);
                console.log('🔥 MAPCORE: Set object visibility to:', newVisibility);
              }
            });
            
            // Handle overlays
            overlays.forEach(overlay => {
              if (overlay && overlay.onToggleVisibility) {
                overlay.onToggleVisibility(newVisibility);
                console.log('🔥 MAPCORE: Set overlay visibility using custom method to:', newVisibility);
              } else if (overlay && overlay.setMap) {
                const targetMap = newVisibility ? googleMapRef.current : null;
                overlay.setMap(targetMap);
                console.log('🔥 MAPCORE: Set overlay visibility to:', newVisibility);
              }
            });
          } else {
            console.warn('🔥 MAPCORE: No objects found for measurement:', measurementId);
          }
          
          const action = newVisibility ? 'SHOWN' : 'HIDDEN';
          console.log(`✅ Measurement ${measurementId} ${action}`);
          
          return { ...measurement, visible: newVisibility };
        }
        return measurement;
      });
      return updated;
    });
    
    // CRITICAL FIX: Clear flag after toggle operation completes
    setTimeout(() => {
      isDirectToggleRef.current = false;
      console.log('🔥 MAPCORE: Flag cleared after toggle operation');
    }, 50);
  };

  const updateMeasurementLabel = (measurementId, newLabel) => {
    console.log('📏 Updating measurement label:', measurementId, newLabel);
    setMeasurements(prev => prev.map(measurement => {
      if (measurement.id === measurementId) {
        return { ...measurement, label: newLabel };
      }
      return measurement;
    }));
  };

  // SIMPLIFIED: Direct visibility toggle without flag system
  const toggleMeasurementVisibilityDirect = (measurementId) => {
    console.log('🔥 SIMPLE TOGGLE called for:', measurementId);
    
    // Get the measurement to know the new visibility state
    const measurement = measurements.find(m => m.id === measurementId);
    if (!measurement) {
      console.error('🔥 Measurement not found:', measurementId);
      return;
    }
    
    const newVisibility = !measurement.visible;
    console.log('🔥 SIMPLE TOGGLE: Current state in data:', measurement.visible, '→ New state:', newVisibility);
    console.log('🔥 SIMPLE TOGGLE: Measurement type:', measurement.type);
    console.log('🔥 SIMPLE TOGGLE: All measurements:', measurements.map(m => ({ id: m.id, type: m.type, visible: m.visible })));
    
    // Find the measurement entry
    const measurementEntry = measurementObjectsRef.current.get(measurementId);
    if (!measurementEntry) {
      console.error('🔥 SIMPLE TOGGLE: No entry found for measurement:', measurementId);
      return;
    }
    
    const { objects = [], overlays = [] } = measurementEntry;
    console.log('🔥 SIMPLE TOGGLE: Found', objects.length, 'objects and', overlays.length, 'overlays');
    
    // DEBUG: Check current visibility state of objects before toggle
    console.log('🔥 SIMPLE TOGGLE: Current object states before toggle:');
    objects.forEach((obj, index) => {
      if (obj && obj.getMap) {
        const currentlyVisible = obj.getMap() !== null;
        console.log(`🔥 SIMPLE TOGGLE: Object ${index} currently visible: ${currentlyVisible}`);
      }
    });
    
    // Toggle visibility for all Google Maps objects
    objects.forEach((obj, index) => {
      if (obj && typeof obj.setMap === 'function') {
        const wasVisible = obj.getMap() !== null;
        obj.setMap(newVisibility ? googleMapRef.current : null);
        const isNowVisible = obj.getMap() !== null;
        console.log(`🔥 SIMPLE TOGGLE: Object ${index} was ${wasVisible}, now ${isNowVisible}, target was ${newVisibility}`);
      }
    });
    
    // Toggle visibility for custom overlays
    overlays.forEach((overlay, index) => {
      if (overlay && overlay.onToggleVisibility) {
        console.log(`🔥 SIMPLE TOGGLE: Overlay ${index} has custom toggle method`);
        overlay.onToggleVisibility(newVisibility);
        console.log(`🔥 SIMPLE TOGGLE: Custom overlay ${index} visibility set to ${newVisibility}`);
      } else if (overlay && overlay.setMap) {
        const wasVisible = overlay.getMap() !== null;
        overlay.setMap(newVisibility ? googleMapRef.current : null);
        const isNowVisible = overlay.getMap() !== null;
        console.log(`🔥 SIMPLE TOGGLE: Overlay ${index} was ${wasVisible}, now ${isNowVisible}, target was ${newVisibility}`);
      }
    });
    
    // Update the state to trigger UI re-render
    setMeasurements(prevMeasurements => 
      prevMeasurements.map(m => 
        m.id === measurementId 
          ? { ...m, visible: newVisibility }
          : m
      )
    );
    
    console.log('🔥 SIMPLE TOGGLE: Complete - no flags, no delays, no race conditions');
  };

  const handleMeasurementClick = (event) => {
    console.log('🎯 handleMeasurementClick called, measurementMode:', measurementMode);
    console.log('🎯 Click event:', event);
    
    if (measurementMode === 'first-click') {
      console.log('🎯 Processing first click');
      // First click - place start marker
      const startMarker = new window.google.maps.Marker({
        position: event.latLng,
        map: googleMapRef.current,
        title: 'Start Point',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 6,
          fillColor: '#ff0000',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2
        }
      });
      
      setMeasurementMarkers([startMarker]);
      setMeasurementData({ startPoint: event.latLng });
      setMeasurementMode('second-click');
      
    } else if (measurementMode === 'second-click') {
      console.log('🎯 Processing second click');
      // Second click - place end marker and calculate distance
      const endMarker = new window.google.maps.Marker({
        position: event.latLng,
        map: googleMapRef.current,
        title: 'End Point',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 6,
          fillColor: '#ff0000',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2
        }
      });
      
      setMeasurementMarkers(prev => [...prev, endMarker]);
      
      // Calculate distance
      let distance;
      try {
        console.log('🔍 DISTANCE CALCULATION DEBUG:');
        console.log('🔍 Google Maps available:', !!window.google);
        console.log('🔍 Geometry library available:', !!window.google?.maps?.geometry);
        console.log('🔍 computeDistanceBetween available:', !!window.google?.maps?.geometry?.spherical?.computeDistanceBetween);
        console.log('🔍 Start point:', measurementData.startPoint);
        console.log('🔍 End point:', event.latLng);
        console.log('🔍 Start point lat():', measurementData.startPoint.lat());
        console.log('🔍 Start point lng():', measurementData.startPoint.lng());
        console.log('🔍 End point lat():', event.latLng.lat());
        console.log('🔍 End point lng():', event.latLng.lng());
        
        if (!window.google?.maps?.geometry?.spherical?.computeDistanceBetween) {
          throw new Error('Google Maps geometry library not available');
        }
        
        distance = window.google.maps.geometry.spherical.computeDistanceBetween(
          measurementData.startPoint,
          event.latLng
        );
        
        console.log('🔍 Calculated distance:', distance);
        
        if (isNaN(distance) || distance === null || distance === undefined) {
          throw new Error('Distance calculation returned invalid value: ' + distance);
        }
      } catch (error) {
        console.error('❌ DISTANCE CALCULATION FAILED:', error);
        // Fallback calculation using Haversine formula
        const lat1 = measurementData.startPoint.lat();
        const lng1 = measurementData.startPoint.lng();
        const lat2 = event.latLng.lat();
        const lng2 = event.latLng.lng();
        
        const R = 6371000; // Earth's radius in meters
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLng/2) * Math.sin(dLng/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        distance = R * c;
        
        console.log('🔄 Fallback distance calculation:', distance);
        
        if (isNaN(distance) || distance === null || distance === undefined) {
          console.error('❌ FALLBACK CALCULATION ALSO FAILED');
          distance = 0; // Last resort
        }
      }
      
      // Create connecting line
      const line = new window.google.maps.Polyline({
        path: [measurementData.startPoint, event.latLng],
        geodesic: true,
        strokeColor: '#ff0000',
        strokeOpacity: 1.0,
        strokeWeight: 3,
        map: googleMapRef.current
      });
      
      setMeasurementLine(line);
      
      // Calculate midpoint for info window
      const midLat = (measurementData.startPoint.lat() + event.latLng.lat()) / 2;
      const midLng = (measurementData.startPoint.lng() + event.latLng.lng()) / 2;
      const midpoint = new window.google.maps.LatLng(midLat, midLng);
      
      // Phase B: Create rotated inline text label for new measurement (consistent with existing measurements)
      const distanceKm = (distance / 1000).toFixed(2);
      const distanceMiles = (distance / 1609.34).toFixed(2);
      
      // Phase B: Calculate rotation angle for new measurement
      const newMeasurementAngle = calculateLineAngle(
        { lat: measurementData.startPoint.lat(), lng: measurementData.startPoint.lng() },
        { lat: event.latLng.lat(), lng: event.latLng.lng() }
      );
      
      // Create custom text overlay for inline distance label
      const textOverlay = new window.google.maps.OverlayView();
      textOverlay.onAdd = function() {
        const div = document.createElement('div');
        div.style.cssText = `
          position: absolute;
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: 3px 6px;
          font-size: 12px;
          font-weight: bold;
          font-family: Arial, sans-serif;
          color: #333;
          pointer-events: none;
          white-space: nowrap;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          line-height: 1.2;
          transform-origin: center center;
        `;
        div.textContent = formatDistance(distance);
        
        this.div = div;
        this.rotationAngle = newMeasurementAngle; // Store rotation angle for draw function
        const panes = this.getPanes();
        panes.overlayMouseTarget.appendChild(div);
      };

      textOverlay.draw = function() {
        const overlayProjection = this.getProjection();
        const position = overlayProjection.fromLatLngToDivPixel(midpoint);
        
        if (this.div && position) {
          // Phase B: Rotated positioning with dynamic angle calculation
          const textWidth = this.div.offsetWidth || 50; // Fallback width
          const textHeight = this.div.offsetHeight || 16; // Fallback height
          
          // Center the text on the line midpoint
          this.div.style.left = (position.x - textWidth / 2) + 'px';
          this.div.style.top = (position.y - textHeight / 2) + 'px';
          
          // Apply rotation transform
          this.div.style.transform = `rotate(${this.rotationAngle}deg)`;
        }
      };

      textOverlay.onRemove = function() {
        if (this.div) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      };

      // 🔥 CRITICAL FIX: Add custom visibility toggle method for GPT-4 solution
      textOverlay.onToggleVisibility = function(isVisible) {
        if (this.div) {
          this.div.style.display = isVisible ? 'block' : 'none';
          console.log('🔥 TEXT OVERLAY: Set visibility to', isVisible ? 'visible' : 'hidden');
        }
      };

      textOverlay.setMap(googleMapRef.current);
      setMeasurementInfoWindow(textOverlay);
      
      setMeasurementData({
        startPoint: measurementData.startPoint,
        endPoint: event.latLng,
        distance: distance
      });
      
      // Phase 1B: Save completed measurement to array (DATA ONLY - no Google Maps objects)
      const measurementId = `line_${measurementIdCounter}`;
      const completedMeasurement = {
        id: measurementId, // Add consistent ID format
        label: `Measurement ${measurementIdCounter}`,
        type: 'line', // Add type field for proper handling
        startPoint: {
          lat: measurementData.startPoint.lat(),
          lng: measurementData.startPoint.lng()
        },
        endPoint: {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        },
        distance: distance,
        distanceKm: distanceKm,
        distanceMiles: distanceMiles,
        timestamp: new Date().toISOString(),
        visible: true
      };
      
      // 🔥 CRITICAL FIX: Don't store temporary objects in measurementObjectsRef
      // Let useEffect handle all permanent object creation and management
      console.log('🔥 CLICK HANDLER: Measurement completed, useEffect will create managed objects');
      
      console.log('📏 Saving measurement to history:', completedMeasurement);
      setMeasurements(prev => [...prev, completedMeasurement]);
      setMeasurementIdCounter(prev => prev + 1);
      
            // 🔥 CRITICAL FIX: Clean up temporary objects immediately (direct references)
      console.log('🔥 CLEANING UP TEMPORARY OBJECTS - Direct cleanup');
      console.log('🔥 measurementMarkers.length:', measurementMarkers.length, '(may be outdated due to async state)');
      console.log('🔥 endMarker exists:', !!endMarker);
      console.log('🔥 line exists:', !!line);
      console.log('🔥 textOverlay exists:', !!textOverlay);
      
      // Clean up ALL temporary objects directly (not relying on state arrays)
      // Clean up all markers from state array (start marker)
      measurementMarkers.forEach((marker, index) => {
        if (marker && marker.setMap) {
          marker.setMap(null);
          console.log(`🔥 REMOVED TEMPORARY MARKER ${index} FROM STATE`);
        }
      });
      
      // Clean up the end marker directly (this is the one missing from state)
      if (endMarker && endMarker.setMap) {
        endMarker.setMap(null);
        console.log('🔥 REMOVED TEMPORARY END MARKER (direct)');
      }
      
      // Clean up the line directly
      if (line && line.setMap) {
        line.setMap(null);
        console.log('🔥 REMOVED TEMPORARY LINE (direct)');
      }
      
      // Clean up the overlay directly
      if (textOverlay && textOverlay.setMap) {
        textOverlay.setMap(null);
        console.log('🔥 REMOVED TEMPORARY OVERLAY (direct)');
      }
      
      // Clear all temporary state
      setMeasurementMarkers([]);
      setMeasurementLine(null);
      setMeasurementInfoWindow(null);
      
      console.log('🔥 CLEANUP COMPLETE - All temporary objects removed directly');
      
      setMeasurementMode('complete');
    }
  };

  // Update measurement functions ref and notify parent when measurement state changes
  useEffect(() => {
    console.log('📏 Updating measurementFunctionsRef.current');
    measurementFunctionsRef.current = {
      measurementMode,
      startMeasurement,
      clearMeasurement: clearCurrentMeasurement,
      // Phase 1B: Multiple measurement functions
      measurements,
      clearAllMeasurements,
      deleteMeasurement,
      toggleMeasurementVisibility,
      toggleMeasurementVisibilityDirect, // 🚨 NEW: Direct toggle (SAME AS CIRCLES!)
      updateMeasurementLabel,
      // Phase 2B-Alpha: Circle measurement functions
      addCircleMeasurement,
      removeCircleMeasurement,
      updateCircleMeasurementVisibility,
      // PHASE 1: Expose circle site detection handler
      handleCircleComplete
    };
    
    // Notify parent of measurement state changes
    if (onMeasurementModeChange) {
      onMeasurementModeChange(measurementMode);
    }
  }, [
    measurementMode, 
    measurements, 
    onMeasurementModeChange,
    startMeasurement,
    clearCurrentMeasurement,
    clearAllMeasurements,
    deleteMeasurement,
    toggleMeasurementVisibility,
    toggleMeasurementVisibilityDirect, // 🚨 NEW: Direct toggle dependency
    updateMeasurementLabel,
    addCircleMeasurement,
    removeCircleMeasurement,
    updateCircleMeasurementVisibility,
    handleCircleComplete
  ]);

  // Handle map loading status
  const renderStatus = (status) => {
    console.log('🗺️ MapCore renderStatus called with:', status, 'Mobile:', window.innerWidth <= 768);
    console.log('🗺️ Status constants:', { LOADING: Status.LOADING, SUCCESS: Status.SUCCESS, FAILURE: Status.FAILURE });
    switch (status) {
      case Status.LOADING:
        console.log('🗺️ Showing loading spinner...');
        return <Spin size="large" />;
      case Status.FAILURE:
        console.log('🗺️ Maps failed to load, showing fallback...');
        return (
          <div className="map-fallback" style={{ 
            height: height || '500px', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            borderRadius: '8px',
            position: 'relative'
          }}>
            <div style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='m0 40l40-40h-40v40zm40 0v-40h-40l40 40z'/%3E%3C/g%3E%3C/svg%3E") repeat`,
              borderRadius: '8px'
            }} />
            <div style={{ zIndex: 1, textAlign: 'center', padding: '20px' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '24px' }}>🗺️ Interactive Map</h3>
              <p style={{ margin: '0 0 8px 0', fontSize: '16px', opacity: 0.9 }}>Australia and New Zealand</p>
              {error?.includes('quota exceeded') ? (
                <>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.8 }}>⚠️ API quota exceeded</p>
                  <p style={{ margin: '0', fontSize: '12px', opacity: 0.7 }}>Please check your Google Cloud billing settings</p>
                </>
              ) : error?.includes('Invalid') ? (
                <>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.8 }}>🔑 Invalid API key</p>
                  <p style={{ margin: '0', fontSize: '12px', opacity: 0.7 }}>Please configure a valid Google Maps API key</p>
                </>
              ) : (
                <>
                  <p style={{ margin: '0', fontSize: '14px', opacity: 0.7 }}>Map service temporarily unavailable</p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.6 }}>Site markers and drawing tools will be available when service is restored</p>
                </>
              )}
            </div>
          </div>
        );
      case Status.SUCCESS:
        console.log('🗺️ SUCCESS status reached - rendering map container');
        console.log('🗺️ Initial center:', initialCenter, 'Zoom:', window.innerWidth <= 768 ? 6 : initialZoom);
        console.log('🔥🔥🔥 MOBILE DETECTION CHECK 🔥🔥🔥');
        console.log('🔥 Screen width:', window.innerWidth);
        console.log('🔥 Is mobile (<=768):', window.innerWidth <= 768);
        console.log('🔥 About to render:', window.innerWidth <= 768 ? 'SIMPLE MOBILE MAP' : 'DESKTOP MAP');
        
        // For mobile, use simple direct map creation with all necessary props
        if (window.innerWidth <= 768) {
          console.log('🔥🔥🔥 RETURNING SIMPLE MOBILE MAP COMPONENT 🔥🔥🔥');
          return <SimpleMobileMap 
            crisisLocation={crisisLocation}
            showSiteIcons={showSiteIcons}
            onSitesSelected={onSitesSelected}
            siteColorMode={siteColorMode}
            onCircleComplete={onCircleComplete}
          />;
        }
        
        console.log('🔥🔥🔥 RETURNING DESKTOP MAP COMPONENT 🔥🔥🔥');
        
        return (
          <div ref={mapContainerRef} style={{ height, width: '100%', position: 'relative' }}>
            <SimpleMapContainer 
              center={initialCenter} 
              zoom={window.innerWidth <= 768 ? 6 : initialZoom} 
              onLoad={initializeMap} 
              style={{ height: '100%', width: '100%' }} 
            />
            
            {/* 🎯 CRITICAL FIX: Restore circle detection popup that was accidentally removed */}
            {showSitePopup && (
              <div style={{ 
                position: 'absolute', 
                top: '20px', 
                right: '20px', 
                zIndex: 1000,
                maxWidth: '400px'
              }}>
                <SiteListPopup
                  sites={circleDetectedSites}
                  circle={activeCircle}
                  onClose={handleCloseSitePopup}
                />
              </div>
            )}
          </div>
        );
      default:
        console.log('🗺️ Unknown status received:', status, 'Type:', typeof status);
        return <div style={{ height, width: '100%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Map Status: {status}</div>;
    }
  };

  // Phase 2C-Alpha: Ring color schemes
  const RING_COLOR_SCHEMES = {
    red: { primary: '#ff0000', rings: '#ff0000' },
    blue: { primary: '#0066cc', rings: '#0066cc' },
    green: { primary: '#00cc66', rings: '#00cc66' },
    purple: { primary: '#9966cc', rings: '#9966cc' },
            orange: { primary: '#fa8c16', rings: '#fa8c16' },
    custom: ringStyleConfig.customColors
  };

  // Phase 2C-Alpha: Get current color scheme
  const getCurrentColors = () => {
    return RING_COLOR_SCHEMES[ringStyleConfig.colorScheme] || RING_COLOR_SCHEMES.red;
  };

  // Phase 2C-Alpha: Get zoom-responsive font size
  const getZoomResponsiveFontSize = (map) => {
    if (!map) return 11;
    
    const zoom = map.getZoom();
    if (zoom >= 18) return 14;
    if (zoom >= 15) return 13;
    if (zoom >= 12) return 12;
    if (zoom >= 10) return 11;
    if (zoom >= 8) return 10;
    return 9;
  };

  // Phase 2C-Alpha: Get zoom-responsive label styling
  const getZoomResponsiveLabelStyle = (map) => {
    if (!map) return { fontSize: '11px', padding: '2px 4px' };
    
    const zoom = map.getZoom();
    const fontSize = getZoomResponsiveFontSize(map);
    
    // Adjust padding and other properties based on zoom
    const padding = zoom >= 15 ? '3px 6px' : zoom >= 12 ? '2px 5px' : '2px 4px';
    const borderRadius = zoom >= 15 ? '4px' : '3px';
    const fontWeight = zoom >= 15 ? '600' : 'bold';
    
    return {
      fontSize: `${fontSize}px`,
      padding,
      borderRadius,
      fontWeight
    };
  };

  // USER LOCATION FUNCTIONS - NEW FEATURE
  const requestUserLocation = () => {
    if (!navigator.geolocation) {
      setUserLocationError('Geolocation is not supported by this browser');
      message.error('Geolocation is not supported by this browser');
      return;
    }

    setRequestingLocation(true);
    setUserLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        
        console.log('📍 User location obtained:', location);
        setUserLocation(location);
        setShowUserLocation(true);
        setRequestingLocation(false);
        
        // Center map on user location
        if (googleMapRef.current) {
          googleMapRef.current.setCenter(location);
          if (googleMapRef.current.getZoom() < 15) {
            googleMapRef.current.setZoom(15);
          }
        }
        
        message.success('Location found successfully');
      },
      (error) => {
        setRequestingLocation(false);
        let errorMessage = 'Unable to retrieve your location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
          default:
            errorMessage = 'An unknown error occurred while retrieving location';
            break;
        }
        
        console.warn('📍 User location error:', errorMessage);
        setUserLocationError(errorMessage);
        message.warning(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  const createUserLocationMarker = (map) => {
    // Remove existing marker if present
    if (userLocationMarker) {
      if (userLocationMarker.setMap) {
        userLocationMarker.setMap(null);
      } else if (userLocationMarker.overlay) {
        userLocationMarker.overlay.setMap(null);
      }
    }

    if (!userLocation) return;

    // Create custom pulsing overlay instead of marker
    const overlay = createPulsingOverlay(map, userLocation);
    setUserLocationMarker({ overlay });
  };

  const getUserLocationIconSize = (map) => {
    if (!map) return 40;
    
    const zoom = map.getZoom();
    // Scale icon based on zoom level - much larger sizes
    if (zoom >= 18) return 60;
    if (zoom >= 15) return 50;
    if (zoom >= 12) return 45;
    if (zoom >= 10) return 40;
    if (zoom >= 8) return 35;
    if (zoom >= 5) return 30;
    return 25;
  };

  const updateUserLocationMarkerScale = () => {
    if (!userLocationMarker?.overlay || !googleMapRef.current) return;
    
    const iconSize = getUserLocationIconSize(googleMapRef.current);
    const element = userLocationMarker.overlay.getDiv();
    if (element) {
      element.style.width = `${iconSize}px`;
      element.style.height = `${iconSize}px`;
      // Update CSS variables for pulsing size
      element.style.setProperty('--size', `${iconSize}px`);
      element.style.setProperty('--pulse-size', `${iconSize * 1.5}px`);
    }
  };

  const createPulsingOverlay = (map, position) => {
    const iconSize = getUserLocationIconSize(map);
    
    // Create custom overlay class
    class PulsingLocationOverlay extends window.google.maps.OverlayView {
      constructor(position, size) {
        super();
        this.position = position;
        this.size = size;
        this.div = null;
      }

      onAdd() {
        const div = document.createElement('div');
        div.style.cssText = `
          position: absolute;
          width: ${this.size}px;
          height: ${this.size}px;
          cursor: pointer;
          z-index: 1000;
        `;
        
        // Create pulsing effect with CSS
        div.innerHTML = `
          <style>
            .user-location-pulse {
              position: relative;
              width: 100%;
              height: 100%;
            }
            .user-location-pulse::before,
            .user-location-pulse::after {
              content: '';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              border-radius: 50%;
              background: #1890ff;
            }
            .user-location-pulse::before {
              width: 100%;
              height: 100%;
              opacity: 0.3;
              animation: pulse1 2s infinite;
            }
            .user-location-pulse::after {
              width: 60%;
              height: 60%;
              opacity: 0.6;
              animation: pulse2 1.5s infinite;
            }
            .user-location-center {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 40%;
              height: 40%;
              background: #1890ff;
              border: 3px solid white;
              border-radius: 50%;
              box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            }
            .user-location-center::after {
              content: '';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 30%;
              height: 30%;
              background: white;
              border-radius: 50%;
            }
            @keyframes pulse1 {
              0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.8; }
              50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.2; }
              100% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.8; }
            }
            @keyframes pulse2 {
              0% { transform: translate(-50%, -50%) scale(0.7); opacity: 0.9; }
              50% { transform: translate(-50%, -50%) scale(1.0); opacity: 0.3; }
              100% { transform: translate(-50%, -50%) scale(0.7); opacity: 0.9; }
            }
          </style>
          <div class="user-location-pulse">
            <div class="user-location-center"></div>
          </div>
        `;

        // Add click listener
        div.addEventListener('click', () => {
          this.showLocationInfo();
        });

        this.div = div;
        const panes = this.getPanes();
        panes.overlayMouseTarget.appendChild(div);
      }

      draw() {
        const overlayProjection = this.getProjection();
        const point = overlayProjection.fromLatLngToDivPixel(this.position);
        
        if (this.div) {
          this.div.style.left = (point.x - this.size / 2) + 'px';
          this.div.style.top = (point.y - this.size / 2) + 'px';
        }
      }

      onRemove() {
        if (this.div) {
          this.div.parentNode.removeChild(this.div);
          this.div = null;
        }
      }

      getDiv() {
        return this.div;
      }

      showLocationInfo() {
        showUserLocationInfo({ position: this.position });
      }
    }

    const overlay = new PulsingLocationOverlay(position, iconSize);
    overlay.setMap(map);
    return overlay;
  };



  const showUserLocationInfo = (overlayData) => {
    if (!infoWindow || !userLocation) return;

    const accuracy = userLocation.accuracy ? Math.round(userLocation.accuracy) : 'Unknown';
    
    const content = `
      <div style="max-width: 200px; font-family: Arial, sans-serif;">
        <h3 style="margin: 0 0 8px 0; color: #1890ff;">📍 Your Location</h3>
        <div style="margin-bottom: 6px;">
          <strong>Coordinates:</strong><br/>
          ${userLocation.lat.toFixed(6)}, ${userLocation.lng.toFixed(6)}
        </div>
        <div style="margin-bottom: 6px;">
          <strong>Accuracy:</strong> ±${accuracy}m
        </div>
        <div style="font-size: 12px; color: #666;">
          Click "Show My Location" to update your position
        </div>
      </div>
    `;

    infoWindow.setContent(content);
    infoWindow.setPosition(userLocation);
    infoWindow.open(googleMapRef.current);
  };

  const toggleUserLocation = () => {
    if (showUserLocation) {
      // Hide user location
      setShowUserLocation(false);
      if (userLocationMarker) {
        if (userLocationMarker.setMap) {
          userLocationMarker.setMap(null);
        } else if (userLocationMarker.overlay) {
          userLocationMarker.overlay.setMap(null);
        }
      }
    } else {
      // Show user location (request if not available)
      if (userLocation) {
        setShowUserLocation(true);
      } else {
        requestUserLocation();
      }
    }
  };

  if (error) {
    return <Alert message={error} type="error" />;
  }

  return (
    <div className={`map-core ${className}`} style={{ height: '100%' }}>
      {/* Map Container */}
      <Card 
        className="map-container"
        styles={{ body: { padding: 0 } }}
        style={{ borderRadius: '8px', overflow: 'hidden', height: '100%' }}
      >
        <div style={{ position: 'relative', height: '100%' }}>
          {isLoading && (
            <div style={{ 
              position: 'absolute', 
              top: '10px', 
              right: '10px', 
              background: 'rgba(255,255,255,0.9)', 
              padding: '4px 8px', 
              borderRadius: '4px', 
              fontSize: '12px',
              zIndex: 1001
            }}>
              Loading sites...
            </div>
          )}
          <Wrapper
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''} // Use empty string if no key provided
            render={renderStatus}
            libraries={['drawing', 'geometry', 'places', 'marker']}
            onError={(error) => {
              console.error('🗺️ Google Maps API Error:', error);
              console.error('🗺️ Error type:', typeof error, 'Error string:', error.toString());
              if (error.toString().includes('OverQuota')) {
                console.error('🗺️ API quota exceeded');
                setError('Google Maps API quota exceeded. Please check your API key billing settings.');
              } else if (error.toString().includes('InvalidKey')) {
                console.error('🗺️ Invalid API key');
                setError('Invalid Google Maps API key. Please check your configuration.');
              } else {
                console.error('🗺️ Generic Maps error');
                setError('Google Maps failed to load. Please check API configuration.');
              }
            }}
          />
          
          {/* USER LOCATION BUTTON - DESKTOP ONLY (removed from mobile per user request) */}
          {window.innerWidth > 768 && (
          <div style={{ 
            position: 'absolute', 
            top: '10px', 
            left: '205px', 
            zIndex: 1000
          }}>
            <Tooltip title={showUserLocation ? 'Hide my location' : 'Show my location'}>
              <Button
                type="default"
                shape="circle"
                icon={<AimOutlined />}
                onClick={toggleUserLocation}
                loading={requestingLocation}
                style={{
                  backgroundColor: showUserLocation ? '#1890ff' : '#fff',
                  color: showUserLocation ? '#fff' : '#1890ff',
                  border: '2px solid #1890ff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </Tooltip>
          </div>
          )}
        </div>
      </Card>


      


      {/* PHASE 1: Site Detection Popup - MOVED TO DASHBOARD LEVEL */}
      
      {/* Individual Site SMS Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MessageOutlined style={{ color: '#39325b' }} />
            <span>Send SMS to Site Manager</span>
          </div>
        }
        open={showIndividualSMSModal}
        onCancel={closeIndividualSMSModal}
        footer={null}
        width={500}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        {selectedSiteForSMS && (
          <>
            {/* Site Information */}
            <div style={{ 
              marginBottom: '16px', 
              padding: '12px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#39325b' }}>
                {selectedSiteForSMS.site_name}
              </h4>
              <div style={{ fontSize: '14px', color: '#666' }}>
                <div><strong>Manager:</strong> {selectedSiteForSMS.site_manager_name}</div>
                <div><strong>Mobile:</strong> {selectedSiteForSMS.site_manager_mobile}</div>
                <div><strong>Type:</strong> {selectedSiteForSMS.site_type?.charAt(0).toUpperCase() + selectedSiteForSMS.site_type?.slice(1)}</div>
              </div>
            </div>

            {/* SMS Templates */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Quick Templates:
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {INDIVIDUAL_SMS_TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    onClick={() => applyIndividualTemplate(template)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#f0f0f0';
                      e.target.style.borderColor = '#39325b';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#fff';
                      e.target.style.borderColor = '#d9d9d9';
                    }}
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            </div>

            {/* SMS Form */}
            <Form
              form={individualSMSForm}
              layout="vertical"
              onFinish={sendIndividualSMS}
            >
              <Form.Item
                name="message"
                label={
                  <span>
                    Message 
                    <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>
                      (GSM-7: 160 chars/segment, Multi-segment cost applies)
                    </span>
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter a message' }
                ]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Enter your message here..."
                  showCount
    
                />
              </Form.Item>

              {/* SMS Segment Indicator */}
              <Form.Item dependencies={['message']} style={{ marginBottom: '16px' }}>
                {({ getFieldValue }) => {
                  const currentMessage = getFieldValue('message');
                  return (
                    <SMSSegmentIndicator 
                      message={currentMessage} 
                      recipientCount={1}
                      showCost={true}
                    />
                  );
                }}
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <Tooltip title="Cancel and close this SMS dialog">
                    <button
                      type="button"
                      onClick={closeIndividualSMSModal}
                      style={{
                        padding: '6px 16px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </Tooltip>
                  <Tooltip title="Send SMS message to the site manager">
                    <button
                      type="submit"
                      disabled={sendingIndividualSMS}
                      style={{
                        padding: '6px 16px',
                        border: 'none',
                        borderRadius: '6px',
                        backgroundColor: sendingIndividualSMS ? '#ccc' : '#39325b',
                        color: 'white',
                        cursor: sendingIndividualSMS ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                    {sendingIndividualSMS ? (
                      <>
                        <span>⏳</span>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <SendOutlined />
                        <span>Send SMS</span>
                      </>
                    )}
                  </button>
                  </Tooltip>
                </div>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

// Simple Map Container Component - works with @googlemaps/react-wrapper
const SimpleMapContainer = ({ center, zoom, onLoad, style }) => {
  const ref = useRef(null);
  const mapCreated = useRef(false);
  
  console.log('🗺️ SimpleMapContainer rendered with props:', { center, zoom, style });
  
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    console.log('🗺️ SimpleMapContainer useEffect triggered', { 
      center, 
      zoom, 
      hasGoogle: !!window.google, 
      isMobile,
      refExists: !!ref.current,
      refDimensions: ref.current ? `${ref.current.offsetWidth}x${ref.current.offsetHeight}` : 'none',
      mapCreated: mapCreated.current
    });
    
    if (ref.current && window.google && !mapCreated.current) {
      console.log('🗺️ All conditions met, creating map...');
      
      // Ensure container has dimensions
      if (ref.current.offsetWidth === 0 || ref.current.offsetHeight === 0) {
        console.log('🗺️ Container has zero dimensions, forcing size...');
        ref.current.style.minHeight = style.height || '400px';
        ref.current.style.minWidth = '100%';
        
        // Retry after setting dimensions
        setTimeout(() => {
          if (ref.current && window.google) {
            createMapInstance();
          }
        }, 50);
      } else {
        createMapInstance();
      }
    } else {
      console.log('🗺️ Conditions not met yet, will retry...');
      // Retry after a short delay in case DOM isn't ready
      const retryTimeout = setTimeout(() => {
        console.log('🗺️ Retry attempt - conditions:', {
          refExists: !!ref.current,
          hasGoogle: !!window.google,
          mapCreated: mapCreated.current
        });
        if (ref.current && window.google && !mapCreated.current) {
          createMapInstance();
        }
      }, 100);
      
      return () => clearTimeout(retryTimeout);
    }
    
    function createMapInstance() {
      try {
        // Force a known working location and zoom for mobile debugging
        const forceCenter = isMobile ? { lat: -37.8136, lng: 144.9631 } : center; // Melbourne for mobile
        const forceZoom = isMobile ? 10 : zoom; // Closer zoom for mobile
        
        const mapOptions = {
          center: forceCenter,
          zoom: forceZoom,
          mapTypeControl: !isMobile,
          streetViewControl: !isMobile, 
          fullscreenControl: !isMobile,
          gestureHandling: 'greedy', // 🎯 FORCE GREEDY FOR ALL DEVICES - BYPASS MOBILE GESTURE RESTRICTIONS
          zoomControl: true,
          disableDefaultUI: false, // Ensure UI is enabled
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        };
        
        console.log('🗺️ Creating map with options:', mapOptions);
        console.log('🗺️ Container final dimensions:', ref.current.offsetWidth, 'x', ref.current.offsetHeight);
        
        // Ensure container has proper dimensions before creating map
        if (ref.current.offsetWidth === 0 || ref.current.offsetHeight === 0) {
          console.warn('🗺️ Container has zero dimensions, forcing size...');
          ref.current.style.width = '100%';
          ref.current.style.height = '400px';
          ref.current.style.minHeight = '400px';
          console.log('🗺️ Forced container dimensions:', ref.current.offsetWidth, 'x', ref.current.offsetHeight);
        }
        
        const map = new window.google.maps.Map(ref.current, mapOptions);
        
        console.log('🗺️ Map created successfully!', map);
        mapCreated.current = true;
        
        // Store in window for debugging
        window.mapInstances = window.mapInstances || [];
        window.mapInstances.push(map);
        
        // Add error listener for tile loading failures
        map.addListener('tilesloaded', () => {
          console.log('🗺️ Map tiles loaded successfully!');
        });
        
        // Force map resize and refresh after creation
        setTimeout(() => {
          console.log('🗺️ Forcing map resize and refresh...');
          window.google.maps.event.trigger(map, 'resize');
          map.setCenter(forceCenter);
          map.setZoom(forceZoom);
          
          // Try forcing roadmap if tiles don't appear
          map.setMapTypeId('roadmap');
          
          // Additional debugging
          console.log('🗺️ Post-resize map state:', {
            center: map.getCenter().toString(),
            zoom: map.getZoom(),
            mapType: map.getMapTypeId(),
            div: map.getDiv()
          });
          
          // Final fallback - try satellite view if roadmap fails
          setTimeout(() => {
            if (ref.current && ref.current.style.backgroundColor === 'rgb(229, 227, 223)') {
              console.log('🗺️ Roadmap appears to be loading, trying satellite...');
              map.setMapTypeId('satellite');
            }
          }, 2000);
        }, 100);
        
        // Call the onLoad callback
        console.log('🗺️ Calling onLoad callback...');
        onLoad(map);
        console.log('🗺️ onLoad callback completed');
        
      } catch (error) {
        console.error('🗺️ Error creating map:', error);
      }
    }
  }, [center, zoom, onLoad]);
  
  return (
    <div 
      ref={ref} 
      style={{
        ...style,
        minHeight: style.height || '400px',
        minWidth: '100%',
        backgroundColor: 'transparent',
        position: 'relative',
        zIndex: 1
      }}
    />
  );
};

  // ENHANCED MOBILE MAP - WITH VIEWPORT VIRTUALIZATION
const SimpleMobileMap = ({ crisisLocation, showSiteIcons = true, onSitesSelected, siteColorMode = 'status', onCircleComplete }) => {
  console.log('🌂 UMBRELLA MOBILE MAP: Rendering with siteColorMode:', siteColorMode);
  console.log('🌂 UMBRELLA PROPS CHECK: Received props -', { siteColorMode, showSiteIcons, timestamp: Date.now() });
  
  const { currentUser } = useAuth();
  const mapRef = useRef(null);
  const [sites, setSites] = useState([]);
  const [orgSiteTypes, setOrgSiteTypes] = useState([]); // CRITICAL FIX: Add missing orgSiteTypes state
  const [markers, setMarkers] = useState([]);
  const [crisisMarker, setCrisisMarker] = useState(null);
  
  // 🎯 CRITICAL FIX: Add missing circle detection states for mobile
  const [showSitePopup, setShowSitePopup] = useState(false);
  const [circleDetectedSites, setCircleDetectedSites] = useState([]);
  const [activeCircle, setActiveCircle] = useState(null);
  
  // 🚨 ADD INCIDENT MARKERS TO MOBILE!
  const [incidents, setIncidents] = useState([]);
  const [incidentMarkers, setIncidentMarkers] = useState([]);
  
  // 🚨 ADD MAP INSTANCE FOR HAZARD OVERLAY WITH DEBUGGING
  const [mapInstance, setMapInstance] = useState(null);
  
  // 🚨 CRITICAL FIX: Add missing mobile map bounds and overlay state
  const [mapBounds, setMapBounds] = useState(null);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [hazardCount, setHazardCount] = useState(0);
  
  // 🚨 DEBUG: Track mapInstance changes for mobile hazard troubleshooting
  useEffect(() => {
    console.log('🚨 MOBILE HAZARD DEBUG: mapInstance changed:', {
      hasMapInstance: !!mapInstance,
      mapInstanceType: typeof mapInstance,
      timestamp: new Date().toLocaleTimeString()
    });
  }, [mapInstance]);
  
  // 🚨 CRITICAL FIX: Track map bounds changes for mobile hazard filtering
  useEffect(() => {
    if (mapInstance) {
      const boundsListener = mapInstance.addListener('bounds_changed', () => {
        const bounds = mapInstance.getBounds();
        if (bounds) {
          setMapBounds({
            north: bounds.getNorthEast().lat(),
            south: bounds.getSouthWest().lat(),
            east: bounds.getNorthEast().lng(),
            west: bounds.getSouthWest().lng()
          });
          console.log('🚨 MOBILE BOUNDS: Map bounds updated for hazard filtering');
        }
      });
      
      return () => {
        if (boundsListener) {
          window.google.maps.event.removeListener(boundsListener);
        }
      };
    }
  }, [mapInstance]);
  
  // 🚀 VIEWPORT VIRTUALIZATION STATE
  const [currentBounds, setCurrentBounds] = useState(null);
  const [visibleSites, setVisibleSites] = useState([]);
  const boundsUpdateTimeoutRef = useRef(null);
  
  console.log('📱 SimpleMobileMap: Rendering with props:', {
    hasCrisisLocation: !!crisisLocation,
    crisisLocation,
    showSiteIcons,
    currentUser: currentUser?.email,
    currentUserOrgId: currentUser?.orgId
  });
  
  console.log('📱 SimpleMobileMap: Current sites state:', sites.length, 'sites');
  console.log('📱 SimpleMobileMap: Current markers state:', markers.length, 'markers');
  console.log('🚀 VIEWPORT: Visible sites in viewport:', visibleSites.length, 'of', sites.length);
  
  // 🚀 VIEWPORT VIRTUALIZATION FUNCTIONS
  const calculateVisibleSites = (bounds, allSites) => {
    if (!bounds || !allSites.length) {
      console.log('🚀 VIEWPORT: No bounds or sites, returning empty array');
      return [];
    }
    
    const north = bounds.getNorthEast().lat();
    const south = bounds.getSouthWest().lat();
    const east = bounds.getNorthEast().lng();
    const west = bounds.getSouthWest().lng();
    
    console.log('🚀 VIEWPORT: Calculating visible sites in bounds:', {
      north: north.toFixed(4),
      south: south.toFixed(4), 
      east: east.toFixed(4),
      west: west.toFixed(4)
    });
    
    const sitesInViewport = allSites.filter(site => {
      if (!site.latitude || !site.longitude) return false;
      
      const lat = parseFloat(site.latitude);
      const lng = parseFloat(site.longitude);
      
      // Check if site is within bounds
      const inBounds = lat >= south && lat <= north && lng >= west && lng <= east;
      return inBounds;
    });
    
    console.log('🚀 VIEWPORT: Found', sitesInViewport.length, 'sites in viewport');
    return sitesInViewport;
  };
  
  const updateViewportMarkers = (map, bounds) => {
    console.log('🚀 VIEWPORT: updateViewportMarkers called');
    
    // Clear existing timeout
    if (boundsUpdateTimeoutRef.current) {
      clearTimeout(boundsUpdateTimeoutRef.current);
    }
    
    // Debounce updates to prevent excessive recalculation during pan/zoom
    boundsUpdateTimeoutRef.current = setTimeout(() => {
      console.log('🚀 VIEWPORT: Debounced update executing...');
      
      const newVisibleSites = calculateVisibleSites(bounds, sites);
      setCurrentBounds(bounds);
      setVisibleSites(newVisibleSites);
      
      // Clear all existing markers
      console.log('🚀 VIEWPORT: Clearing', markers.length, 'existing markers');
      markers.forEach(marker => {
        marker.setMap(null);
      });
      setMarkers([]);
      
      // Create markers only for visible sites
      if (newVisibleSites.length > 0 && showSiteIcons) {
        console.log('🚀 VIEWPORT: Creating markers for', newVisibleSites.length, 'visible sites with siteColorMode:', siteColorMode);
        createViewportSiteMarkers(map, newVisibleSites);
      }
    }, 200); // 200ms debounce
  };
  
  // Load organization sites
  useEffect(() => {
    console.log('🔥 SimpleMobileMap: useEffect for site loading triggered');
    console.log('🔥 currentUser:', currentUser);
    console.log('🔥 currentUser.orgId:', currentUser?.orgId);
    
    const loadSites = async () => {
      if (currentUser?.orgId) {
        try {
          console.log('🔥 SimpleMobileMap: Starting site loading for org:', currentUser.orgId);
          
          // CRITICAL FIX: Load BOTH sites AND orgSiteTypes like main MapCore does
          const [orgSites, orgTypes] = await Promise.all([
            getSitesByOrganization(currentUser.orgId),
            getOrgSiteTypes(currentUser.orgId)
          ]);
          
          console.log('🔥 SimpleMobileMap: Loaded', orgSites.length, 'sites and', orgTypes.length, 'site types');
          setSites(orgSites);
          setOrgSiteTypes(orgTypes); // CRITICAL FIX: Set orgSiteTypes state
          console.log('🔥 SimpleMobileMap: Sites and orgSiteTypes state updated');
        } catch (error) {
          console.error('🔥 SimpleMobileMap: Error loading sites:', error);
          setSites([]);
          setOrgSiteTypes([]); // CRITICAL FIX: Reset orgSiteTypes on error
        }
      } else {
        console.log('🔥 SimpleMobileMap: No orgId, skipping site loading');
      }
    };
    
    loadSites();
  }, [currentUser?.orgId]);

  // 🚨 LOAD INCIDENTS FOR MOBILE MAP
  useEffect(() => {
    console.log('🚨 SimpleMobileMap: useEffect for incident loading triggered');
    
    const loadIncidents = async () => {
      if (currentUser?.orgId) {
        try {
          console.log('🚨 SimpleMobileMap: Loading incidents for org:', currentUser.orgId);
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const { db } = await import('../../config/firebase');
          
          const incidentsRef = collection(db, 'c3_incidents');
          const q = query(
            incidentsRef,
            where('orgId', '==', currentUser.orgId)
          );
          const querySnapshot = await getDocs(q);
          const incidentsList = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Filter to only active incidents with locations
          const activeIncidentsWithLocation = incidentsList.filter(incident => 
            incident.status !== 'resolved' && 
            incident.status !== 'deleted' &&
            incident.latitude && 
            incident.longitude &&
            !isNaN(incident.latitude) && 
            !isNaN(incident.longitude)
          );
          
          console.log('🚨 SimpleMobileMap: Loaded', incidentsList.length, 'total incidents,', activeIncidentsWithLocation.length, 'with locations');
          setIncidents(activeIncidentsWithLocation);
        } catch (error) {
          console.error('🚨 SimpleMobileMap: Error loading incidents:', error);
          setIncidents([]);
        }
      }
    };
    
    loadIncidents();
  }, [currentUser?.orgId]);
  
  useEffect(() => {
    console.log('🔥 SimpleMobileMap: useEffect for map creation triggered');
    console.log('🔥 Window.google available:', !!window.google);
    console.log('🔥 Window.google.maps available:', !!window.google?.maps);
    
    // Wait for Google Maps to be ready
    if (!window.google || !window.google.maps) {
      console.log('🔥 Waiting for Google Maps API...');
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps) {
          console.log('🔥 Google Maps API now available, creating map...');
          clearInterval(checkGoogle);
          createSimpleMap();
        }
      }, 100);
      return () => clearInterval(checkGoogle);
    } else {
      console.log('🔥 Google Maps API already available, creating map immediately...');
      createSimpleMap();
    }
    
    function createSimpleMap() {
      console.log('🔥🔥🔥 createSimpleMap FUNCTION CALLED 🔥🔥🔥');
      
      if (!mapRef.current) {
        console.log('🔥 ERROR: mapRef.current is null or undefined');
        return;
      }
      
      console.log('🔥 mapRef.current exists:', mapRef.current);
      console.log('🔥 Creating map with crisis location:', crisisLocation);
      
      // Force container positioning
      mapRef.current.style.width = '100%';
      mapRef.current.style.height = '400px';
      mapRef.current.style.position = 'absolute';
      mapRef.current.style.top = '0';
      mapRef.current.style.left = '0';
      
      try {
        // Set initial center based on crisis location or default to Melbourne
        let initialCenter = { lat: -37.8136, lng: 144.9631 }; // Melbourne default
        let initialZoom = 12;
        
        if (crisisLocation && crisisLocation.latitude && crisisLocation.longitude) {
          initialCenter = { lat: crisisLocation.latitude, lng: crisisLocation.longitude };
          initialZoom = 15; // Zoom closer for crisis location
          console.log('🔥 Using crisis location as center:', initialCenter);
        } else {
          console.log('🔥 Using default Melbourne center:', initialCenter);
        }
        
        console.log('🔥 Map options:', { center: initialCenter, zoom: initialZoom });
        
        const mapOptions = {
          center: initialCenter,
          zoom: initialZoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy', // 🎯 FORCE ALLOW SINGLE FINGER DRAWING
          zoomControl: true
        };
        
        console.log('🔥 Creating Google Maps instance...');
        const map = new window.google.maps.Map(mapRef.current, mapOptions);
        console.log('🔥 Google Maps instance created:', map);
        
        // Store map reference for markers
        window.simpleMobileMapInstance = map;
        
        // 🚨 CRITICAL FIX: Set map instance for hazard overlay WITH IMMEDIATE VALIDATION
        console.log('🚨 MOBILE HAZARD FIX: Setting map instance for hazard overlay...');
        setMapInstance(map);
        
        // 🚨 CRITICAL FIX: Validate map instance was set correctly AND trigger initial bounds
        setTimeout(() => {
          console.log('🚨 MOBILE HAZARD VALIDATION: Map instance state check...');
          console.log('🚨 Map instance set?', !!map);
          console.log('🚨 Map instance in window?', !!window.simpleMobileMapInstance);
          console.log('🚨 Map instance ready for hazards?', map && map.getCenter);
          
          // Force a re-check of mapInstance state
          if (map && map.getCenter) {
            console.log('🚨 MOBILE HAZARD SUCCESS: Map is fully ready for hazard overlay');
            setMapInstance(map); // Set again to ensure React state is updated
            
            // 🚨 CRITICAL FIX: Trigger initial bounds for hazard filtering
            const initialBounds = map.getBounds();
            if (initialBounds) {
              setMapBounds({
                north: initialBounds.getNorthEast().lat(),
                south: initialBounds.getSouthWest().lat(),
                east: initialBounds.getNorthEast().lng(),
                west: initialBounds.getSouthWest().lng()
              });
              console.log('🚨 MOBILE HAZARD SUCCESS: Initial bounds set for hazard filtering');
            }
          } else {
            console.error('🚨 MOBILE HAZARD ERROR: Map instance not properly initialized');
          }
        }, 500);
        
        // Add bounds change listener for viewport virtualization AND hazard filtering
        map.addListener('bounds_changed', () => {
          console.log('🚀 VIEWPORT: Map bounds changed, updating viewport markers...');
          const bounds = map.getBounds();
          if (bounds) {
            // Update viewport markers
            updateViewportMarkers(map, bounds);
            
            // 🚨 CRITICAL FIX: Update bounds for hazard filtering
            setMapBounds({
              north: bounds.getNorthEast().lat(),
              south: bounds.getSouthWest().lat(),
              east: bounds.getNorthEast().lng(),
              west: bounds.getSouthWest().lng()
            });
          }
        });
        
        console.log('🔥 Checking sites and crisis location for marker creation...');
        console.log('🔥 Sites available:', sites.length, 'sites');
        console.log('🔥 Crisis location available:', !!crisisLocation);
        console.log('🔥 showSiteIcons:', showSiteIcons);
        
        // Create crisis location marker if provided
        if (crisisLocation && crisisLocation.latitude && crisisLocation.longitude) {
          console.log('🔥 Creating crisis location marker...');
          createCrisisLocationMarker(map);
        } else {
          console.log('🔥 No crisis location to create marker for');
        }
        
        // 🚨 CREATE INCIDENT MARKERS FOR MOBILE MAP
        if (incidents.length > 0) {
          console.log('🚨 Creating incident markers for', incidents.length, 'incidents');
          createIncidentMarkers(map);
        } else {
          console.log('🚨 No incidents to create markers for');
        }
        
        // 🚀 VIEWPORT VIRTUALIZATION: Initialize viewport markers
        console.log('🚀 VIEWPORT: Initializing viewport-based markers...');
        console.log('🚀 VIEWPORT: Sites available for viewport:', sites.length);
        console.log('🚀 VIEWPORT: showSiteIcons:', showSiteIcons);
        
        if (sites.length > 0 && showSiteIcons) {
          // Get initial bounds and create markers for visible sites
          const initialBounds = map.getBounds();
          if (initialBounds) {
            console.log('🚀 VIEWPORT: Got initial bounds, calculating visible sites...');
            updateViewportMarkers(map, initialBounds);
          } else {
            console.log('🚀 VIEWPORT: No initial bounds yet, will wait for bounds_changed');
          }
        } else {
          console.log('🚀 VIEWPORT: No sites or showSiteIcons=false, skipping marker creation');
        }
        
        console.log('🔥🔥🔥 createSimpleMap COMPLETE 🔥🔥🔥');
        
      } catch (error) {
        console.error('🔥 ERROR in createSimpleMap:', error);
      }
    }
  }, [crisisLocation, showSiteIcons, sites, incidents]);
  
  // 🚨 CREATE INCIDENT MARKERS FOR MOBILE MAP
  const createIncidentMarkers = (map) => {
    console.log('🚨 createIncidentMarkers called for', incidents.length, 'incidents');
    
    // Clear existing incident markers
    incidentMarkers.forEach(marker => marker.setMap(null));
    setIncidentMarkers([]);
    
    const newIncidentMarkers = incidents.map((incident, index) => {
      console.log(`🚨 Creating incident marker ${index + 1}/${incidents.length} for:`, incident.title);
      
      try {
        const marker = new window.google.maps.Marker({
          position: { lat: parseFloat(incident.latitude), lng: parseFloat(incident.longitude) },
          map: map,
          title: `Incident: ${incident.title}`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 18, // Bigger than sites
            fillColor: '#ff4444', // Red for incidents
            fillOpacity: 1,
            strokeWeight: 3,
            strokeColor: '#FFFFFF'
          },
          zIndex: 2000 // Higher than sites
        });
        
        // Add click listener for incident info
        marker.addListener('click', () => {
          console.log('🚨 Incident marker clicked:', incident.title);
          showMobileIncidentInfo(incident, map, marker);
        });
        
        console.log(`🚨 Incident marker ${index + 1} created successfully`);
        return marker;
        
      } catch (error) {
        console.error(`🚨 Error creating incident marker for ${incident.title}:`, error);
        return null;
      }
    }).filter(marker => marker !== null);
    
    console.log('🚨 Created', newIncidentMarkers.length, 'incident markers');
    setIncidentMarkers(newIncidentMarkers);
  };
  
  // 🚨 MOBILE INCIDENT INFO POPUP
  const showMobileIncidentInfo = (incident, map, marker) => {
    console.log('🚨 Showing mobile incident info for:', incident.title);
    
    const content = `
      <div style="
        max-width: 320px; 
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        overflow: hidden;
      ">
        <div style="
          background: linear-gradient(135deg, #ff4444 0%, #cc3333 100%);
          color: white;
          padding: 12px 16px;
          margin: -8px -8px 12px -8px;
        ">
          <h3 style="margin: 0; font-size: 16px; font-weight: 600;">
            🚨 ${incident.title}
          </h3>
        </div>
        
        <div style="padding: 0 16px 16px 16px;">
          <div style="margin-bottom: 8px;">
            <strong style="color: #666;">Type:</strong> 
            <span style="color: #333;">${incident.crisisType || incident.type || 'Unknown'}</span>
          </div>
          
          <div style="margin-bottom: 8px;">
            <strong style="color: #666;">Status:</strong> 
            <span style="
              background: ${getIncidentStatusColor(incident.status)};
              color: white;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 500;
              text-transform: uppercase;
            ">
              ${incident.status || 'Unknown'}
            </span>
          </div>
          
          ${incident.location ? `
            <div style="margin-bottom: 8px;">
              <strong style="color: #666;">Location:</strong><br/>
              <span style="color: #333; font-size: 14px;">${incident.location}</span>
            </div>
          ` : ''}
          
          ${incident.description ? `
            <div style="margin-bottom: 12px;">
              <strong style="color: #666;">Description:</strong><br/>
              <span style="color: #333; font-size: 14px; line-height: 1.4;">
                ${incident.description.length > 100 ? incident.description.substring(0, 100) + '...' : incident.description}
              </span>
            </div>
          ` : ''}
          
          <div style="
            border-top: 1px solid #eee; 
            padding-top: 12px; 
            margin-top: 12px;
            text-align: center;
          ">
            <a href="/incidents/${incident.id}" style="
              background: linear-gradient(135deg, #1890ff 0%, #0066cc 100%);
              color: white;
              text-decoration: none;
              padding: 8px 16px;
              border-radius: 6px;
              font-size: 13px;
              font-weight: 500;
              display: inline-block;
              box-shadow: 0 2px 4px rgba(24, 144, 255, 0.3);
            ">
              📋 View Details
            </a>
          </div>
        </div>
      </div>
    `;
    
    const infoWindow = new window.google.maps.InfoWindow({
      content: content
    });
    
    infoWindow.open(map, marker);
  };

  // Helper function for incident status colors
  const getIncidentStatusColor = (status) => {
    const colors = {
      'active': '#ff4444',
      'stabilising': '#fa8c16', 
      'resolved': '#52c41a',
      'exercise': '#722ed1',
      'unknown': '#8c8c8c'
    };
    return colors[status?.toLowerCase()] || colors.unknown;
  };
  
  // Create crisis location marker
  const createCrisisLocationMarker = (map) => {
    if (!crisisLocation || !crisisLocation.latitude || !crisisLocation.longitude) return;
    
    console.log('📱 SimpleMobileMap: Creating crisis location marker:', crisisLocation);
    
    // Clear existing crisis marker
    if (crisisMarker) {
      crisisMarker.setMap(null);
    }
    
    const marker = new window.google.maps.Marker({
      position: { lat: crisisLocation.latitude, lng: crisisLocation.longitude },
      map: map,
      title: `Crisis Location: ${crisisLocation.title || 'Incident Location'}`,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="28" height="35" viewBox="0 0 28 35" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 0C6.3 0 0 6.3 0 14c0 7.7 14 21 14 21s14-13.3 14-21C28 6.3 21.7 0 14 0z" 
                  fill="#ff4444" stroke="#000" stroke-width="2"/>
            <text x="14" y="18" text-anchor="middle" font-family="Arial" font-size="10" fill="white" font-weight="bold">!</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(28, 35),
        anchor: new window.google.maps.Point(14, 35)
      },
      zIndex: 1000 // Ensure crisis marker appears above other markers
    });
    
    // Add click listener for crisis info
    marker.addListener('click', () => {
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; max-width: 250px;">
            <h4 style="margin: 0 0 8px 0; font-size: 14px; color: #ff4444;">
              🚨 ${crisisLocation.title || 'Crisis Location'}
            </h4>
            <p style="margin: 0; font-size: 12px; color: #666;">
              ${crisisLocation.address || 'Incident location'}
            </p>
            ${crisisLocation.formattedAddress ? `
              <p style="margin: 4px 0 0 0; font-size: 11px; color: #999;">
                ${crisisLocation.formattedAddress}
              </p>
            ` : ''}
          </div>
        `
      });
      infoWindow.open(map, marker);
    });
    
    setCrisisMarker(marker);
    console.log('📱 SimpleMobileMap: Crisis marker created');
  };
  
  // 🚀 VIEWPORT: Update viewport markers when sites or showSiteIcons change
  useEffect(() => {
    // Mobile site markers useEffect
    // eslint-disable-next-line no-undef
    console.log('🌂 UMBRELLA useEffect: Updating markers, mode:', siteColorMode, 'sites:', sites.length, 'orgSiteTypes:', orgSiteTypes ? orgSiteTypes.length : 'undefined');
    console.log('🌂 UMBRELLA useEffect TRIGGER: About to call createViewportSiteMarkers with mode:', siteColorMode);
    
    if (window.simpleMobileMapInstance) {
      if (!showSiteIcons) {
        // Clear all site markers when disabled
        markers.forEach(marker => marker.setMap(null));
        setMarkers([]);
        setVisibleSites([]);
      // eslint-disable-next-line no-undef
      } else if (sites.length > 0 && orgSiteTypes && Array.isArray(orgSiteTypes)) {
        // CRITICAL FIX: Wait for BOTH sites AND orgSiteTypes to be ready
        // Clear existing markers
        markers.forEach(marker => marker.setMap(null));
        setMarkers([]);
        
        // Create new markers for ALL sites (like desktop)
        createViewportSiteMarkers(window.simpleMobileMapInstance, sites);
        } else {
        // No sites available OR orgSiteTypes not ready yet, clearing markers
        markers.forEach(marker => marker.setMap(null));
        setMarkers([]);
        setVisibleSites([]);
      }
    } else {
      // No map instance available yet
    }
  // eslint-disable-next-line no-undef
  }, [sites, showSiteIcons, siteColorMode, orgSiteTypes]);
  
  // Update crisis marker when crisis location changes
  useEffect(() => {
    if (window.simpleMobileMapInstance && crisisLocation) {
      createCrisisLocationMarker(window.simpleMobileMapInstance);
    } else if (window.simpleMobileMapInstance && !crisisLocation && crisisMarker) {
      // Clear crisis marker if no crisis location
      crisisMarker.setMap(null);
      setCrisisMarker(null);
    }
  }, [crisisLocation]);
  
  // 🚨 UPDATE INCIDENT MARKERS WHEN INCIDENTS CHANGE
  useEffect(() => {
    if (window.simpleMobileMapInstance && incidents.length > 0) {
      console.log('🚨 Updating incident markers for', incidents.length, 'incidents');
      createIncidentMarkers(window.simpleMobileMapInstance);
    } else if (window.simpleMobileMapInstance && incidents.length === 0) {
      // Clear incident markers if no incidents
      incidentMarkers.forEach(marker => marker.setMap(null));
      setIncidentMarkers([]);
    }
  }, [incidents]);
  
  // 🎯 CRITICAL FIX: Add circle detection functions to SimpleMobileMap
  const detectSitesInCircle = (circle) => {
    if (!circle || !sites || sites.length === 0) {
      console.log('🎯 MOBILE: No circle or sites to detect');
      return [];
    }

    const center = circle.getCenter();
    const radiusMeters = circle.getRadius();
    
    console.log('🎯 MOBILE: Detecting sites in circle:', {
      center: { lat: center.lat(), lng: center.lng() },
      radiusMeters: radiusMeters,
      totalSites: sites.length
    });

    const sitesInCircle = sites.filter(site => {
      if (!site.latitude || !site.longitude) {
        return false;
      }

      // Calculate distance using Haversine formula
      const distance = calculateHaversineDistance(
        center.lat(), center.lng(),
        site.latitude, site.longitude
      );

      return distance <= radiusMeters;
        });
        
    console.log('🎯 MOBILE: Sites detected in circle:', sitesInCircle.length);
    return sitesInCircle;
  };
        
  // Haversine distance calculation
  const calculateHaversineDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in meters
  };
        
  // Handle circle completion for mobile
  const handleMobileCircleComplete = (circle) => {
    console.log('🎯 MOBILE: Circle completed, detecting sites...');
    const detectedSites = detectSitesInCircle(circle);
    
    setCircleDetectedSites(detectedSites);
    setActiveCircle(circle);
    setShowSitePopup(true);
    
    console.log('🎯 MOBILE: Circle detection complete:', detectedSites.length, 'sites found');
  };

  // Handle closing mobile site popup
  const handleCloseMobileSitePopup = () => {
    setShowSitePopup(false);
    setCircleDetectedSites([]);
    setActiveCircle(null);
  };
  
  // 🖱️ MOBILE SITE INFO POPUP (like desktop version)
  const showMobileSiteInfo = (site, map, marker) => {
    console.log('🖱️ Showing mobile site info for:', site.site_name);
    
    const content = `
      <div style="
        max-width: 320px; 
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        overflow: hidden;
      ">
        <div style="
          background: linear-gradient(135deg, ${getStatusColor(site.status)} 0%, ${darkenColor(getStatusColor(site.status))} 100%);
          color: white;
          padding: 12px 16px;
          margin: -8px -8px 12px -8px;
        ">
          <h3 style="margin: 0; font-size: 16px; font-weight: 600;">
            🏢 ${site.site_name}
          </h3>
        </div>
        
        <div style="padding: 0 16px 16px 16px;">
          <div style="margin-bottom: 8px;">
            <strong style="color: #666;">Type:</strong> 
            <span style="color: #333;">${site.site_type ? site.site_type.charAt(0).toUpperCase() + site.site_type.slice(1) : 'Unknown'}</span>
          </div>
          
          <div style="margin-bottom: 8px;">
            <strong style="color: #666;">Status:</strong> 
            <span style="
              background: ${getStatusColor(site.status)};
              color: white;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 500;
              text-transform: uppercase;
            ">
              ${getStatusLabel(site.status)}
            </span>
          </div>
          
          <div style="margin-bottom: 8px;">
            <strong style="color: #666;">Address:</strong><br/>
            <span style="color: #333; font-size: 14px; line-height: 1.4;">
              ${site.address_line_1 || 'No address available'}${site.address_line_2 ? '<br/>' + site.address_line_2 : ''}<br/>
              ${site.suburb || ''}, ${site.state || ''} ${site.postcode || ''}
            </span>
          </div>
          
          ${site.region ? `
            <div style="margin-bottom: 8px;">
              <strong style="color: #666;">Region:</strong> 
              <span style="color: #333;">${site.region}</span>
            </div>
          ` : ''}
          
          ${site.site_manager_name ? `
            <div style="
              border-top: 1px solid #eee; 
              padding-top: 12px; 
              margin-top: 12px;
            ">
              <div style="margin-bottom: 8px;">
                <strong style="color: #666;">Site Manager:</strong> 
                <span style="color: #333;">${site.site_manager_name}</span>
              </div>
              
              <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                ${site.site_manager_mobile ? `
                  <a href="tel:${site.site_manager_mobile}" style="
                    background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
                    color: white;
                    text-decoration: none;
                    padding: 6px 12px;
                    border-radius: 6px;
                    font-size: 12px;
                    font-weight: 500;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    box-shadow: 0 2px 4px rgba(82, 196, 26, 0.3);
                  ">
                    📞 Call
                  </a>
                ` : ''}
                
                ${site.site_manager_email ? `
                  <a href="mailto:${site.site_manager_email}" style="
                    background: linear-gradient(135deg, #1890ff 0%, #0066cc 100%);
                    color: white;
                    text-decoration: none;
                    padding: 6px 12px;
                    border-radius: 6px;
                    font-size: 12px;
                    font-weight: 500;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    box-shadow: 0 2px 4px rgba(24, 144, 255, 0.3);
                  ">
                    ✉️ Email
                  </a>
                ` : ''}
              </div>
            </div>
          ` : ''}
        </div>
      </div>
    `;
    
    const infoWindow = new window.google.maps.InfoWindow({
      content: content
    });
    
    infoWindow.open(map, marker);
  };

  // Helper functions for site status
  const getStatusColor = (status) => {
    const colors = {
      'active': '#27ae60',
      'degraded': '#f39c12', 
      'evacuating': '#e74c3c',
      'evacuated': '#9b59b6',
      'closed_and_secure': '#3498db',
      'unknown': '#95a5a6'
    };
    return colors[status] || colors.unknown;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'active': 'Active',
      'degraded': 'Degraded', 
      'evacuating': 'Evacuating',
      'evacuated': 'Evacuated',
      'closed_and_secure': 'Closed & Secure',
      'unknown': 'Unknown'
    };
    return labels[status] || 'Unknown';
  };

  // Helper function to darken a color
  const darkenColor = (color) => {
    // Simple darkening - reduce each RGB component
    const colorMap = {
      '#27ae60': '#1e8449',
      '#f39c12': '#d68910', 
      '#e74c3c': '#c0392b',
      '#9b59b6': '#8e44ad',
      '#3498db': '#2980b9',
      '#95a5a6': '#7f8c8d'
    };
    return colorMap[color] || color;
  };

  // 🚀 CREATE MARKERS FOR VIEWPORT SITES ONLY - Fixed closure issue with useCallback
  const createViewportSiteMarkers = useCallback((map, sitesToRender) => {
    console.log('🌂 UMBRELLA Creating', sitesToRender.length, 'markers in mode:', siteColorMode);
    console.log('🌂 UMBRELLA CLOSURE CHECK: siteColorMode captured in useCallback:', siteColorMode);
    
    const newMarkers = sitesToRender.map((site, index) => {
      console.log(`🚀 VIEWPORT: Creating marker ${index + 1}/${sitesToRender.length} for site:`, site.site_name);
      
      try {
        // 🎨 MOBILE SITE COLORS: Support both status and type modes
        const statusColors = {
          'active': '#27ae60',     // Green
          'degraded': '#f39c12',   // Orange  
          'evacuating': '#e74c3c', // Red
          'evacuated': '#9b59b6',  // Purple
          'closed_and_secure': '#3498db', // Blue
          'unknown': '#95a5a6'     // Gray
        };
        
        // 🎨 Dynamic type colors from organization's registered site types (matches MapLegend)
        const typeColors = {};
        // eslint-disable-next-line no-undef
        if (orgSiteTypes && Array.isArray(orgSiteTypes)) {
          // eslint-disable-next-line no-undef
          orgSiteTypes.forEach(type => {
            typeColors[type.name] = type.color;
          });
        }
        // Fallback for unregistered types
        typeColors['unknown'] = '#95a5a6';
        
        // Choose color source based on siteColorMode
        const colorValue = siteColorMode === 'status' 
          ? statusColors[site.status] || statusColors.unknown
          : typeColors[site.site_type] || typeColors.unknown;
        
        // 🔍 CRITICAL DEBUG: Only log TYPE issues for first 3 sites
        if (siteColorMode === 'type' && index < 3) {
          console.log(`🌂 UMBRELLA TYPE: Site ${index + 1}: "${site.site_type}" → ${colorValue}`);
          if (index === 0) {
            console.log(`🌂 UMBRELLA TYPE: Available keys:`, Object.keys(typeColors));
          }
        }
        
        // 🏢 MOBILE BUILDING ICON: Custom SVG path for building/facility
        // 🏢 MOBILE SITE ICON OPTIONS - Using Location Pin (Professional & Clear)
        // Option 1: Location Pin (CURRENT CHOICE)
        const locationPinPath = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
        
        // Option 2: Shield (Alternative - uncomment to use)
        // const shieldPath = 'M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10.5V11.5C15.4,11.5 16,12.4 16,13V16C16,17.1 15.1,18 14,18H10C8.9,18 8,17.1 8,16V13C8,12.4 8.4,11.5 9,11.5V10.5C9,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.2,9.2 10.2,10.5V11.5H13.8V10.5C13.8,9.2 12.8,8.2 12,8.2Z';
        
        // Option 3: Simple Dot (Alternative - uncomment to use)  
        // const dotPath = 'M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z';
        
        const bulletproofIcon = {
          path: locationPinPath, // Clean location pin for sites
          scale: 1.0,
          fillColor: colorValue,
          fillOpacity: 0.9,
          strokeWeight: 2,
          strokeColor: '#FFFFFF',
          anchor: { x: 12, y: 24 } // Pin bottom to exact location
        };
        
        console.log(`🏢 MOBILE SITE: Creating pin marker for:`, site.site_name, 
                   `Mode: ${siteColorMode}`, 
                   `${siteColorMode === 'status' ? 'Status:' : 'Type:'} ${siteColorMode === 'status' ? site.status : site.site_type}`, 
                   'Color:', bulletproofIcon.fillColor);
        
        const marker = new window.google.maps.Marker({
          position: { lat: site.latitude, lng: site.longitude },
          map: map,
          title: `${site.site_name} (${site.status})`,
          icon: bulletproofIcon,
          zIndex: 999
        });
        
        // 🖱️ ADD DESKTOP-STYLE CLICK INFO POPUP
        marker.addListener('click', () => {
          console.log('🚀 VIEWPORT: Site marker clicked:', site.site_name);
          showMobileSiteInfo(site, map, marker);
        });
        
        console.log(`🚀 VIEWPORT: Marker ${index + 1} created successfully:`, marker);
        return marker;
        
      } catch (error) {
        console.error(`🚀 VIEWPORT: Error creating marker for site ${site.site_name}:`, error);
        return null;
      }
    }).filter(marker => marker !== null);
    
    console.log('🚀 VIEWPORT: Created', newMarkers.length, 'viewport markers successfully');
    setMarkers(newMarkers);
  // eslint-disable-next-line no-undef
  }, [siteColorMode, orgSiteTypes]);
  
  return (
    <div style={{ 
      width: '100%', 
      height: '400px', 
      position: 'relative', 
      overflow: 'hidden'
    }}>
      <div 
        ref={mapRef} 
        style={{ 
          width: '100%', 
          height: '100%', 
          position: 'absolute', 
          top: 0, 
          left: 0 
        }} 
      />
      
      {/* 🚨 MOBILE HAZARD OVERLAY - NO CONTROLS, JUST HAZARDS */}
      {mapInstance && (
        <HazardOverlayLayer
          map={mapInstance}
          mapBounds={mapBounds}
          showControls={false}
          overlayVisible={overlayVisible}
          onHazardClick={(hazard) => {
            console.log('🚨 Mobile hazard clicked:', hazard);
          }}
          onHazardCountChange={(count) => {
            console.log('🚨 Mobile hazard count changed:', count);
            setHazardCount(count);
          }}
        />
      )}
      
      {/* 🎯 CRITICAL FIX: Add circle detection popup to mobile */}
      {showSitePopup && (
        <div style={{ 
          position: 'absolute', 
          top: '20px', 
          right: '20px', 
          zIndex: 1000,
          maxWidth: '300px'
        }}>
          <SiteListPopup
            sites={circleDetectedSites}
            circle={activeCircle}
            onClose={handleCloseMobileSitePopup}
          />
        </div>
      )}

    </div>
  );
};

export default MapCore; 